import React, { useState, useMemo, useEffect, useRef, useCallback } from 'react';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, ReferenceLine,
} from 'recharts';
import { getMarketVolatility, recalcPeriodDetail, checkStability, getStabilityProgress,
         extendStabilityPeriod, togglePeriodBlock,
         extendPeriod, runVariantLadder, getVariantProgress, getActiveVariant,
         getAnalyzerResult } from '../api';
import { usePersistentPageSize } from '../hooks/usePersistentPageSize';
import { useLocalStorage } from '../hooks/useLocalStorage';
import { useIsMobile } from '../hooks/useIsMobile';
import StrategyParamsLabel from './StrategyParamsLabel';
import PeriodsTable from './analyzer/PeriodsTable';
import { buildTickerTradeContext } from '../lib/tradeContext';

// ── Light theme tokens ────────────────────────────────────────────────────────
const C = {
  pageBg:    '#F1F5F9',
  cardBg:    '#FFFFFF',
  border:    '#E2E8F0',
  text:      '#0F172A',
  textMd:    '#475569',
  textSm:    '#94A3B8',
  primary:   '#2563EB',
  primaryLt: '#EFF6FF',
  success:   '#16A34A',
  successLt: '#DCFCE7',
  danger:    '#DC2626',
  dangerLt:  '#FEE2E2',
  buyBg:     '#EFF6FF',
  sellBg:    '#F0FDF4',
  shadow:    '0 1px 3px rgba(0,0,0,0.08)',
};

const S = {
  page:    { background: C.pageBg, minHeight: '100vh', fontFamily: "'Inter','Segoe UI',system-ui,sans-serif", color: C.text },
  wrap:    { maxWidth: 1300, margin: '0 auto', padding: '24px 28px' },
  card:    { background: C.cardBg, border: `1px solid ${C.border}`, borderRadius: 12, boxShadow: C.shadow, marginBottom: 20 },
  // Back bar
  backBar: { display: 'flex', alignItems: 'center', gap: 12, marginBottom: 20 },
  backBtn: {
    display: 'inline-flex', alignItems: 'center', gap: 6,
    padding: '7px 16px', background: 'transparent', color: C.primary,
    border: `1px solid ${C.primary}`, borderRadius: 7, fontSize: 13, fontWeight: 500, cursor: 'pointer',
  },
  title:   { fontSize: 18, fontWeight: 700, color: C.text },
  titleSub:{ fontSize: 12, color: C.textMd, marginTop: 3 },
  // Metrics compact rows
  metricsGrid:  { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 0, padding: '0' },
  metricsGrid4: { display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 14, padding: '0 20px 18px' },
  metCard:  { background: C.pageBg, borderRadius: 8, padding: '12px 14px', border: `1px solid ${C.border}` },
  metLabel: { fontSize: 11, color: C.textSm, textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 6 },
  metValue: { fontSize: 20, fontWeight: 700, color: C.text },
  metSub:   { fontSize: 11, color: C.textMd, marginTop: 2 },
  metRow:   { display: 'flex', alignItems: 'baseline', gap: 6, padding: '7px 20px', borderBottom: `1px solid ${C.border}` },
  metRowLabel: { fontSize: 12, color: C.textSm, whiteSpace: 'nowrap', minWidth: 110 },
  metRowValue: { fontSize: 14, fontWeight: 700, color: C.text },
  metRowsGrid: { display: 'grid', gridTemplateColumns: '1fr 1fr' },
  // Chart
  chartHead: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '14px 20px', borderBottom: `1px solid ${C.border}`, flexWrap: 'wrap', gap: 10 },
  chartTitle:{ fontWeight: 600, fontSize: 14 },
  filterRow: { display: 'flex', gap: 6 },
  filterBtn: (active) => ({
    padding: '5px 12px', borderRadius: 6, fontSize: 12, cursor: 'pointer', fontWeight: 500,
    background: active ? C.primary : 'transparent',
    color: active ? '#fff' : C.textMd,
    border: `1px solid ${active ? C.primary : C.border}`,
  }),
  modeBtn: (active) => ({
    padding: '4px 10px', borderRadius: 5, fontSize: 12, cursor: 'pointer',
    background: active ? '#1E293B' : 'transparent',
    color: active ? '#fff' : C.textMd,
    border: `1px solid ${active ? '#1E293B' : C.border}`,
  }),
  // Table
  tableHead: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '14px 20px', borderBottom: `1px solid ${C.border}`, flexWrap: 'wrap', gap: 10 },
  tableTitle:{ fontWeight: 600, fontSize: 14 },
  tabRow:    { display: 'flex', gap: 6 },
  tabBtn: (active) => ({
    padding: '5px 14px', borderRadius: 6, fontSize: 12, cursor: 'pointer', fontWeight: 500,
    background: active ? C.primary : 'transparent',
    color: active ? '#fff' : C.textMd,
    border: `1px solid ${active ? C.primary : C.border}`,
  }),
  table:  { borderCollapse: 'collapse', width: '100%', fontSize: 12 },
  th: { padding: '10px 14px', background: '#F8FAFC', border: `1px solid ${C.border}`, color: C.textMd, fontWeight: 600, textAlign: 'left', whiteSpace: 'nowrap' },
  td: { padding: '9px 14px', border: `1px solid ${C.border}`, color: C.text, whiteSpace: 'nowrap', verticalAlign: 'middle' },
  pagRow: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 20px', borderTop: `1px solid ${C.border}` },
  btnSm: {
    padding: '5px 10px', background: 'transparent', color: C.primary,
    border: `1px solid ${C.border}`, borderRadius: 6, fontSize: 12, cursor: 'pointer',
  },
};

const RANGE_FILTERS = ['7D', 'MTD', 'YTD', '1Y', 'All'];
const PAGE_SIZE_OPTIONS = [10, 50, 100, 500, 1000, 'all'];

function fmtUsd(n) { return n != null ? Number(n).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' $' : '—'; }

// Trade time as market (ET) wall-clock "YYYY-MM-DD HH:MM". Live trades arrive as
// absolute ISO instants with an offset ("2026-07-27T10:04:28.960988-04:00") — project
// to America/New_York; backtest trades are already naive ET strings — just tidy them.
const _ET_TRADE_DTF = new Intl.DateTimeFormat('en-US', {
  timeZone: 'America/New_York', hour12: false,
  year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit',
});
function fmtTradeTime(s) {
  if (!s) return '—';
  const str = String(s);
  if (/(?:Z|[+-]\d{2}:?\d{2})$/.test(str)) {
    const p = {};
    for (const part of _ET_TRADE_DTF.formatToParts(new Date(str)))
      if (part.type !== 'literal') p[part.type] = part.value;
    let h = p.hour; if (h === '24') h = '00';
    return `${p.year}-${p.month}-${p.day} ${h}:${p.minute}`;
  }
  return str.replace('T', ' ').slice(0, 16); // naive ET (backtest) → tidy
}
function fmtPct(n, showSign = true) {
  if (n == null) return '—';
  const s = showSign && n >= 0 ? '+' : '';
  return s + Number(n).toFixed(2) + '%';
}
function fmtNum(n, dec = 3) { return n != null ? Number(n).toFixed(dec) : '—'; }
const VAR_AXES_DEFAULT = {
  capital:   { on: true,  from: 50000, to: 250000, step: 50000 },
  companies: { on: false, from: 2,     to: 20,     step: 2 },
  leverage:  { on: false, from: 1,     to: 2,      step: 0.25 },
  // Hold to Cost [3]: значення — HTC Bars; withOff додає щабель «без HTC»
  // (те саме, що прапорець «Не враховувати Hold to Cost (baseline)» у формі).
  hold_to_cost: { on: false, from: 100, to: 600, step: 100, withOff: true },
};

function mergeVarAxes(saved) {
  const out = {};
  for (const [k, def] of Object.entries(VAR_AXES_DEFAULT)) {
    const v = saved && typeof saved === 'object' ? saved[k] : null;
    out[k] = { ...def, ...(v && typeof v === 'object' ? v : {}) };
  }
  return out;
}

function fmtAnnualPct(n, dec = 1) {
  if (n == null || !isFinite(n)) return '—';
  // Аннуалізація над вікном у хвилини/години дає космічні числа (e+296%) —
  // це не інформація, а артефакт короткого вікна: ховаємо за '—'.
  if (Math.abs(n) >= 10000) return '—';
  const sign = n >= 0 ? '+' : '';
  return sign + Number(n).toFixed(dec) + '%';
}

function pearsonCorr(xs, ys) {
  const n = xs.length;
  if (n < 3) return null;
  const mx = xs.reduce((a, b) => a + b, 0) / n;
  const my = ys.reduce((a, b) => a + b, 0) / n;
  let num = 0, dx2 = 0, dy2 = 0;
  for (let i = 0; i < n; i++) {
    const dx = xs[i] - mx, dy = ys[i] - my;
    num += dx * dy; dx2 += dx * dx; dy2 += dy * dy;
  }
  const denom = Math.sqrt(dx2 * dy2);
  return denom === 0 ? 0 : Math.round(num / denom * 100) / 100;
}

function fmtDuration(sellTime, buyTime) {
  if (!buyTime) return '—';
  const diff = new Date(sellTime) - new Date(buyTime);
  if (diff <= 0) return '—';
  const minutes = Math.floor(diff / 60000);
  const hours   = Math.floor(minutes / 60);
  const days    = Math.floor(hours / 24);
  if (days >= 1) return `${days}д ${hours % 24}г`;
  if (hours >= 1) return `${hours}г ${minutes % 60}хв`;
  return `${minutes}хв`;
}

// RTH hourly bars per trading day. The grid keeps 7 bars/day (09:30, 10:00 … 15:00
// ET); a bar/hour count maps to *trading* days at this rate.
const BARS_PER_DAY = 7;

// Calendar days per trading day (≈365/252) — for translating long bar-count
// lookback windows into the calendar span a user sees on the chart's X-axis.
const CAL_PER_TRADING_DAY = 1.45;

// Converts a bar/hour count to a "Nд Mг" string on the 7-bars/trading-day basis.
//   16 → "2д 2г",  1 → "1г"   (used for short configured/measured windows)
function barsToDH(bars) {
  if (bars == null || isNaN(Number(bars))) return '?';
  const b = Number(bars);
  if (b <= 0) return '0г';
  const days = Math.floor(b / BARS_PER_DAY);
  const remH = Math.round(b - days * BARS_PER_DAY);
  if (days <= 0) return `${remH}г`;
  if (remH <= 0) return `${days}д`;
  return `${days}д ${remH}г`;
}

// Approximate *calendar* span of a long hourly lookback window (dip / overpriced),
// which spans weeks–months and is compared against the chart's calendar X-axis.
// Prefixed "~": the exact span varies per ticker (data gaps/holidays); the engine
// uses the raw bar count, not this label.
//   530 bars → "~110 дн"   (vs the old misleading "81д 4г")
function barsToCalDays(bars, dataType = 'hourly') {
  if (dataType !== 'hourly' && dataType !== '5m' && dataType !== '1m') {
    return fmtWin(bars, dataType);   // daily windows are already in calendar days
  }
  const b = Number(bars);
  if (b == null || isNaN(b) || b <= 0) return '?';
  if (b < BARS_PER_DAY) return `${Math.round(b)}г`;          // intraday → hours
  const calDays = Math.round((b / BARS_PER_DAY) * CAL_PER_TRADING_DAY);
  return `~${calDays} дн`;
}

// Formats a window value (bars for hourly/intraday, days for daily) for display.
function fmtWin(val, dataType = 'hourly') {
  if (val == null) return '?';
  if (dataType === 'hourly' || dataType === '5m' || dataType === '1m') {
    return barsToDH(val);
  }
  return `${val}д`;
}

// "Starting sum" = what we paid to open the position.
//   BUY row  → the purchase amount (qty × buy price = t.sum)
//   SELL row → the original buy amount (qty × buy price)
function startSum(t) {
  if (t.action === 'BUY') return t.sum != null ? t.sum : null;
  if (t.buy_price != null && t.qty != null) return t.buy_price * t.qty;
  if (t.sum != null && t.change_usd != null) return t.sum - t.change_usd;
  return t.sum != null ? t.sum : null;
}

// "Final sum" = proceeds when the position is closed. Undefined for open BUY rows.
function finalSum(t) {
  return t.action === 'BUY' ? null : (t.sum != null ? t.sum : null);
}

function getDetails(trade, params) {
  const dt    = params.data_type || 'hourly';
  const dur   = trade.buy_time && trade.time ? fmtDuration(trade.time, trade.buy_time) : null;
  const xBuyT = params.x_buy ?? params.buy_pct;
  const ySellT = params.y_sell ?? params.sell_pct;
  const tBuyT  = params.t_hours_buy ?? params.buy_window_days ?? params.t_buy;
  const tSellT = params.sell_window_days ?? params.t_hours_sell ?? tBuyT;
  const hsT   = params.hard_stop_pct;
  const fmtT  = (v) => v != null ? fmtWin(v, dt) : '?';                 // configured windows
  const fmtB  = (v) => (v != null && Number(v) >= 0) ? fmtWin(v, dt) : '?'; // bar counts
  const fmtLB = (v) => (v != null && Number(v) >= 0) ? barsToCalDays(v, dt) : '?'; // long lookback windows → calendar days
  const fmtAbsP = (v) => v == null ? '?' : Math.abs(Number(v)).toFixed(2) + '%';
  const fmtP    = (v) => v == null ? '?' : (v >= 0 ? '+' : '') + Number(v).toFixed(2) + '%';

  // Every buy filter that had to pass for this entry, most-restrictive first.
  // The actual dip% (buy_dip_pct) is only known if the result was computed with
  // the dip-aware engine — otherwise we show '?' alongside the threshold.
  const buyConditions = () => {
    const parts = [];
    if (params.dip_filter_enabled) {
      parts.push(`Впало на ${fmtAbsP(trade.buy_dip_pct)} (≥${params.dip_pct ?? '?'}%) за ${fmtLB(params.dip_period_bars)}`);
    }
    // The growth window ("за Nг") is only known when the engine logged the bar
    // count. The live bot logs only the % (not the bars), so omit the segment
    // rather than printing "за ?". Backtest/virtual still show it (bars present).
    const buyWin = (trade.buy_growth_bars != null && Number(trade.buy_growth_bars) >= 0)
      ? ` за ${fmtB(trade.buy_growth_bars)} (≥${fmtT(tBuyT)})` : '';
    parts.push(`Зріст ${fmtAbsP(trade.buy_signal_pct)} (≥${xBuyT ?? '?'}%)${buyWin}`);
    if (params.buy_cap_pct > 0) {
      parts.push(`Зріст ≤${params.buy_cap_pct}%`);
    }
    if (params.overpriced_pct > 0) {
      parts.push(`Не дорожче MA +${params.overpriced_pct}% за ${fmtLB(params.overpriced_period_bars)}`);
    }
    return parts.join(' + ');
  };

  // Outcome word for the "no growth" exit (price stayed below the buy threshold
  // for the full sell window). Reflects the realised price direction — never the
  // old "Слабкий ріст" wording.
  const outcomeWord = (pnl) => pnl == null ? 'Зміна' : pnl < 0 ? 'Падіння' : pnl > 0 ? 'Зріст' : 'Стагнація';

  switch (trade.description) {
    case 'signal':
    case 'signal_window':
      return buyConditions();
    case 'signal_intraday':
      return `Зріст intraday ${fmtAbsP(trade.buy_signal_pct)} (≥${params.intraday_pct ?? '?'}%) від відкриття`;
    case 'reversal_signal':
      return `Reversal сигнал`;
    case 'drop_in_period': {
      // Live bot logs the drop % but not the drop-window bars → omit "за N" when
      // absent instead of "за ?". Backtest/virtual keep it (bars present).
      const dropWin = (trade.sell_drop_bars != null && trade.sell_drop_bars >= 0)
        ? ` за ${fmtB(trade.sell_drop_bars)} (≤${fmtT(tSellT)})` : '';
      return `Падіння ${fmtAbsP(trade.sell_drop_pct)} (≥${ySellT ?? '?'}%)${dropWin} — тримали ${dur ?? '?'}`;
    }
    case 'stop_loss':
      return `Падіння ${fmtP(trade.pnl_pct)} (≤−${ySellT ?? '?'}%) — тримали ${dur ?? '?'}`;
    case 'no_growth':
      return `${outcomeWord(trade.pnl_pct)} ${fmtP(trade.pnl_pct)} (не виріс на ≥${xBuyT ?? '?'}% за ${fmtT(tSellT)}) — тримали ${dur ?? '?'}`;
    case 'hard_stop':
    case 'hard_stop_loss':
      return `Падіння ${fmtP(trade.pnl_pct)} (≤−${hsT ?? '?'}% hard stop) — тримали ${dur ?? '?'}`;
    case 'stagnation':
      return `Стагнація [${params.stagnation_min_pct ?? -0.5}%..${params.stagnation_max_pct ?? 0.5}%] протягом ${fmtT(params.stagnation_bars)} — тримали ${dur ?? '?'}`;
    case 'overnight_protection':
    case 'risk_protection':
      return `Захист перед ніччю/вихідними: ${fmtP(trade.pnl_pct)} — тримали ${dur ?? '?'}`;
    case 'regime_change':
      return `Зміна режиму: ${fmtP(trade.pnl_pct)} — тримали ${dur ?? '?'}`;
    case 'rotation':
      return `Ротація — продаж заради сильнішого сигналу: ${fmtP(trade.pnl_pct)} — тримали ${dur ?? '?'}`;
    case 'opportunity':
      return `Закриття під кращу можливість: ${fmtP(trade.pnl_pct)} — тримали ${dur ?? '?'}`;
    case 'take_profit':
      return `Take-profit: ${fmtP(trade.pnl_pct)} (≥${params.take_profit_pct ?? params.tp_pct ?? '?'}%) — тримали ${dur ?? '?'}`;
    case 'delisting':
      // R3d: не звичайний продаж, а подія — компанія зникла з біржі. Ціна 0
      // означає списання (виключення за невідповідність вимогам), ненульова —
      // виплату за останньою біржовою котировкою (поглинання, погашення SPAC,
      // або банкрут, що поїхав торгуватись на позабіржовий ринок).
      return (Number(trade.price) === 0
        ? `Делістинг: компанію виключено з біржі, позицію списано в нуль ${fmtP(trade.pnl_pct)} — тримали ${dur ?? '?'}`
        : `Делістинг: закрито за останньою біржовою ціною ${fmtP(trade.pnl_pct)} — тримали ${dur ?? '?'}`);
    case 'deleverage':
      // Не рішення стратегії, а вимушений продаж: власний капітал упав нижче
      // порога підтримки, і рушій розвантажує позиції до безпечного рівня.
      // Продає з найкращого P&L першим, мінімально можливу кількість акцій.
      return `Примусовий продаж під маржею (капітал упав нижче порога підтримки): ${fmtP(trade.pnl_pct)} — тримали ${dur ?? '?'}`;
    case 'tp_fraction':
      // Менеджер 15.10, опція [2d]: ціль Take Profit ще не досягнута, але
      // прибуток уже дійшов до заданої частки, а гроші потрібні під покупку.
      return `Продаж під покупку за часткою Take Profit (≥${params.swap_tp_frac_pct ?? '?'}% від цілі ${params.take_profit_pct ?? '?'}%): ${fmtP(trade.pnl_pct)} — тримали ${dur ?? '?'}`;
    case 'swap_buy':
      // Покупка, на яку гроші дав продаж позиції на цьому ж барі ([2c]/[2d]).
      return 'Покупка під продаж (гроші — від позиції, проданої на цьому ж барі)';
    case 'slice_fill':
      // ТЗ-31: позиція набирається частинами, бо цілу заявку не поглинає денний
      // обіг. Ціна входу по позиції — зважена середня всіх частин, а вік позиції
      // рахується від ПЕРШОЇ частини, не від цієї.
      return 'Добір частини позиції (ціле замовлення не вміщується в денний обіг)';
    case 'end_of_period':
      return `Кінець бектесту (тримали ${dur ?? '?'})`;
    default:
      return trade.description || '—';
  }
}

function CustomTooltip({ active, payload, label, mode }) {
  if (!active || !payload?.length) return null;
  return (
    <div style={{ background: '#fff', border: `1px solid ${C.border}`, borderRadius: 8, padding: '8px 12px', fontSize: 12, boxShadow: C.shadow }}>
      <div style={{ color: C.textMd, marginBottom: 4 }}>{label}</div>
      {payload.map((p, i) => {
        if (p.dataKey === 'vol_index') return (
          <div key={i} style={{ fontWeight: 600, color: '#F59E0B', marginTop: i > 0 ? 2 : 0 }}>
            Vol Index: {p.value != null ? p.value.toFixed(1) : '—'}
          </div>
        );
        return (
          <div key={i} style={{ fontWeight: 700, color: C.primary }}>
            {mode === 'value' ? fmtUsd(p.value) : fmtPct(p.value)}
          </div>
        );
      })}
    </div>
  );
}

// Драбину умов (плече × обіг) прибрано 08.09; колонка `margin_runs` у базі лишилась.
// «Перевірку стійкості» повернуто 15.09.

// `nested` — цей екземпляр намальовано ВСЕРЕДИНІ іншого BacktestDetail (деталізація
// періоду, див. self-render у кінці файлу). Відрізняється від `embedded`, який
// означає «показ у кабінеті бота»: там інші підписи й вимкнені оверлеї. Тут же
// компонент повноцінний, просто не має дублювати блоки рівня всього результату.
export default function BacktestDetail({ data, onBack, onTickerClick, onRecalc, onToggleStar, onCopyToForm, onRecalcPeriod, onPushToTrading, pushTargets, pushTarget, onPushTargetChange, embedded = false, nested = false }) {
  const isMobile = useIsMobile();
  const [rangeFilter, setRangeFilter]     = useLocalStorage('bd_rangeFilter', 'All');
  const [chartMode, setChartMode]         = useLocalStorage('bd_chartMode', 'value');
  const [tradeFilter, setTradeFilter]     = useLocalStorage('bd_tradeFilter', 'ALL');
  const [tradePage, setTradePage]         = useState(0);
  const [sortCol, setSortCol]             = useLocalStorage('bd_sortCol', 'id');
  const [sortDir, setSortDir]             = useLocalStorage('bd_sortDir', 'asc');
  const [pageSize, setPageSize]           = usePersistentPageSize('pg_backtest', 50);
  const [showVolatility, setShowVolatility] = useLocalStorage('bd_showVolatility', false);
  const [showTrend,      setShowTrend]      = useLocalStorage('bd_showTrend', false);
  const [volData,        setVolData]        = useState([]);
  const [volLoading,     setVolLoading]     = useState(false);
  // R6: перевірка стійкості — на вимогу, не колонкою (одна перевірка ≈ 10
  // симуляцій, а рядків у пошуку тисячі).
  // Результати тепер живуть у рядку БД (`stability_runs`), а не лише в стані
  // компонента: раніше вони зникали з оновленням сторінки і перевірку доводилось
  // ганяти заново. Кожен запуск бере нове зерно, тож перевіряти можна багато
  // разів — усі запуски лишаються, а середні рахуються по всіх разом.
  const [stabRuns,    setStabRuns]    = useState(() =>
    Array.isArray(data?.stability_runs) ? data.stability_runs : []);
  const [stabLoading, setStabLoading] = useState(false);
  const [stabError,   setStabError]   = useState('');
  const [stabOpen,    setStabOpen]    = useState(null);   // номер розгорнутого запуску
  const [stabProgress, setStabProgress] = useState(null); // {done,total,rows} під час лічби
  const [stabExtendBusy, setStabExtendBusy] = useState(null);
  // Налаштування над кнопкою. Менше 12 місяців бекенд не приймає: коротше вікно
  // міряє епізод, а не стратегію.
  const [stabCfg, setStabCfg] = useLocalStorage('bd_stabCfg', {
    months: 12, count: 20, extraEnabled: false, extraFrom: 0, extraTo: 90,
    capitalLadder: true, mode: 'random',
  });
  const stabSweep = (stabCfg.mode || 'random') === 'sweep';

  // Рядок міг прийти після першого рендера (або змінитись при переході між
  // результатами) — історія перевірок їде разом з ним.
  useEffect(() => {
    setStabRuns(Array.isArray(data?.stability_runs) ? data.stability_runs : []);
    setStabOpen(null);
    setStabError('');
  }, [data?.id, data?.stability_runs]);

  // Перевірки, зняті ДО 28.08, міряли інші осі (капітал / склад всесвіту /
  // нарізка головного вікна) і мають іншу форму запису. Змішувати їх у підсумок
  // не можна — числа непорівнянні, тож вони лише рахуються окремим рядком.
  const stabRunsV2 = useMemo(
    () => stabRuns.filter(r => r && r.kind === 'periods_v2'), [stabRuns]);
  const stabRunsLegacy = useMemo(
    () => stabRuns.filter(r => r && r.kind !== 'periods_v2'), [stabRuns]);
  const [stabLegacyOpen, setStabLegacyOpen] = useState(false);

  // Перевірка рахується у ФОНІ на бекенді: 20 випадкових вікон — це десятки
  // хвилин, а один довгий запит уперся б у proxy_read_timeout. Тут лише
  // опитування прогресу; закрита вкладка розрахунок не вбиває, а кожен
  // порахований рядок уже лежить у базі.
  const stabPollRef = useRef(null);
  const pollStability = useCallback((rowId) => {
    if (stabPollRef.current) clearInterval(stabPollRef.current);
    stabPollRef.current = setInterval(async () => {
      try {
        const res = await getStabilityProgress(rowId);
        const d = res.data || {};
        // 🚨 «queued» — ТЕЖ робота, що триває. Перевірку рахує окремий процес,
        // і поки задача стоїть у черзі (наприклад, за багатоденним перебором),
        // статус саме такий. Без цієї гілки опитування зупинялось би одразу
        // після запуску, кнопка поверталась би — і людина тиснула б її ще раз.
        const busy = d.status === 'running' || d.status === 'queued';
        setStabProgress(busy
          ? { done: d.done || 0, total: d.total || 0, rows: d.rows || [],
              capitalRows: d.capital_rows || [], phase: d.phase || d.status }
          : null);
        if (!busy) {
          clearInterval(stabPollRef.current);
          stabPollRef.current = null;
          setStabLoading(false);
          if (Array.isArray(d.runs)) setStabRuns(d.runs);
          if (d.status === 'error') setStabError(d.detail || 'Помилка перевірки');
          else setStabOpen(d.run ?? null);
        }
      } catch (e) {
        clearInterval(stabPollRef.current);
        stabPollRef.current = null;
        setStabLoading(false);
        setStabError(e?.response?.data?.detail || e.message || 'Помилка перевірки');
      }
    }, 4000);
  }, []);

  useEffect(() => () => { if (stabPollRef.current) clearInterval(stabPollRef.current); }, []);

  // Перевірка живе на сервері, тож перезавантаження сторінки (чи повернення в
  // закриту вкладку) має підхопити її, а не показувати кнопку так, ніби нічого
  // не рахується.
  useEffect(() => {
    const rowId = data?.id;
    if (!rowId || embedded || nested) return;
    let cancelled = false;
    getStabilityProgress(rowId).then(res => {
      const st = res.data?.status;
      if (cancelled || (st !== 'running' && st !== 'queued')) return;
      setStabLoading(true);
      setStabProgress({ done: res.data.done || 0, total: res.data.total || 0,
                        rows: res.data.rows || [], phase: res.data.phase || st });
      pollStability(rowId);
    }).catch(() => {});
    return () => { cancelled = true; };
  }, [data?.id, embedded, nested, pollStability]);

  const runStabilityCheck = useCallback(async () => {
    const rowId = data?.id;
    if (!rowId) { setStabError('У цього рядка немає id — перевірка недоступна'); return; }
    setStabLoading(true); setStabError(''); setStabProgress({ done: 0, total: 0, rows: [] });
    try {
      await checkStability(rowId, {
        period_months:      Number(stabCfg.months),
        n_periods:          Number(stabCfg.count),
        extra_days_enabled: !!stabCfg.extraEnabled,
        extra_days_from:    Number(stabCfg.extraFrom) || 0,
        extra_days_to:      Number(stabCfg.extraTo) || 0,
        capital_ladder:     !!stabCfg.capitalLadder,
        mode:               stabSweep ? 'sweep' : 'random',
      });
      pollStability(rowId);
    } catch (e) {
      setStabLoading(false);
      setStabError(e?.response?.data?.detail || e.message || 'Помилка перевірки');
    }
  }, [data?.id, stabCfg, stabSweep, pollStability]);

  // «+» на рядку: той самий початок, вікно довше на N місяців. Сенс — саме
  // збиткові вікна: збитковий рік, продовжений на другий, майже завжди стає
  // прибутковим, і це варто бачити поруч із самим збитком.
  const extendStability = useCallback(async (period, idx, monthsFromRow) => {
    const rowId = data?.id;
    if (!rowId || !period?.start) return;
    const months = parseInt(monthsFromRow, 10);
    if (!months || months < 1) { setStabError('Некоректна кількість місяців'); return; }
    setStabExtendBusy(idx); setStabError('');
    try {
      const res = await extendStabilityPeriod(rowId, period.start, months);
      if (Array.isArray(res.data?.runs)) setStabRuns(res.data.runs);
    } catch (e) {
      setStabError(e?.response?.data?.detail || e.message || 'Не вдалось продовжити період');
    } finally {
      setStabExtendBusy(null);
    }
  }, [data?.id]);

  // Trend + Market Volatility overlays are analyzer-only tools. On the embedded
  // user bot view we hide the toggle buttons AND force the overlays off, so a
  // stale localStorage flag (bd_showTrend/bd_showVolatility) can't leak them in.
  const trendOn = showTrend      && !embedded;
  const volOn   = showVolatility && !embedded;
  const [tickerStatsCollapsed, setTickerStatsCollapsed] = useLocalStorage('bd_tickerStatsCollapsed', false);
  const [tickerSortCol, setTickerSortCol]   = useLocalStorage('bd_tickerSortCol', 'total');
  const [tickerSortDir, setTickerSortDir]   = useLocalStorage('bd_tickerSortDir', 'desc');
  const [recalcLoading, setRecalcLoading]   = useState(false);
  const [periodsRecalcProgress, setPeriodsRecalcProgress] = useState(null); // {done,total} or null
  const [highlightedTradeIdx, setHighlightedTradeIdx] = useState(null); // index in sortedTrades
  const [tickerFilter, setTickerFilter] = useState(null); // filter trades by ticker
  const highlightedRowRef = useRef(null);
  const tradesCardRef     = useRef(null);

  // 🚨 Оголошення стоїть саме тут, а не поруч із рештою коду блоків нижче:
  // `periods` — перший споживач, і оголошення нижче за нього дає TDZ, тобто
  // сторінка не будується взагалі («Cannot access ... before initialization»).
  // Той самий клас, що вже ловився в periodsForTable з periodDays.
  //
  // Свіжий стан вікон після перемикання блоку — щоб таблиця й підсумок
  // оновились без перезавантаження сторінки. Скидається зі зміною рядка.
  const [periodsPatch, setPeriodsPatch] = useState(null);
  useEffect(() => { setPeriodsPatch(null); }, [data?.id]);
  const effectivePeriods = periodsPatch
    || (Array.isArray(data.periods_results) ? data.periods_results : []);
  const periods = effectivePeriods;
  // The main window already has its own dedicated "Main" tab, so drop the is_main
  // entry from the period list (it lives in periods_results at index 0 since the
  // "all periods equal" rework — without this it shows up as a duplicate P-tab).
  // Keep each kept period's ORIGINAL index — recalcPeriodDetail/state maps index
  // into the full periods_results array.
  const extraPeriods = periods
    .map((p, idx) => ({ p, idx }))
    .filter(x => !(x.p && x.p.is_main));
  const [activeTabRaw, setActiveTab] = useLocalStorage(`bd_activeTab_${data.id}`, 0);
  const activeTab = Math.min(activeTabRaw, extraPeriods.length);

  // Hydrate persisted per-period drill-down (trades+equity+metrics) so the chart
  // survives a refresh. Keyed by numeric period index.
  const _hydratePeriodDetails = (d) => {
    const pd = d?.period_details || {};
    const out = {};
    for (const k of Object.keys(pd)) out[Number(k)] = pd[k];
    return out;
  };
  const [periodFullDetails, setPeriodFullDetails] = useState(() => _hydratePeriodDetails(data));
  useEffect(() => { setPeriodFullDetails(_hydratePeriodDetails(data)); }, [data.id]); // eslint-disable-line react-hooks/exhaustive-deps
  const [loadingPeriods, setLoadingPeriods]        = useState(new Set());
  const [periodErrors, setPeriodErrors]            = useState({});
  // Persisted summaries echoed back by the recalc — override the (stale) prop so
  // Sharpe/MaxDD/Buy stay filled even after collapsing the full detail.
  const [periodOverrides, setPeriodOverrides]      = useState({});

  const handlePeriodRecalc = async (periodIdx) => {
    setLoadingPeriods(prev => new Set([...prev, periodIdx]));
    setPeriodErrors(prev => { const n = {...prev}; delete n[periodIdx]; return n; });
    try {
      const resp = await recalcPeriodDetail(data.id, periodIdx);
      setPeriodFullDetails(prev => ({...prev, [periodIdx]: resp.data}));
      if (resp.data?.period_summary) {
        setPeriodOverrides(prev => ({...prev, [periodIdx]: resp.data.period_summary}));
      }
    } catch (e) {
      const status = e.response?.status;
      let msg;
      if (status === 408) msg = 'Sim timed out (>120s)';
      else if (status === 404) msg = 'Period not available';
      else if (status === 422) msg = 'Invalid period';
      else msg = e.response?.data?.detail || e.message || 'Recalc failed';
      setPeriodErrors(prev => ({...prev, [periodIdx]: msg}));
    } finally {
      setLoadingPeriods(prev => { const n = new Set(prev); n.delete(periodIdx); return n; });
    }
  };

  const clearPeriodDetail = (periodIdx) => {
    setPeriodFullDetails(prev => { const n = {...prev}; delete n[periodIdx]; return n; });
    setPeriodErrors(prev => { const n = {...prev}; delete n[periodIdx]; return n; });
  };

  const params  = data.params  || {};
  // Some filter params (dip/overpriced/volume) live in _meta — merge so the trade
  // detail strings see them, matching what StrategyParamsLabel renders.

  const detailParams = useMemo(() => ({ ...params, ...(data._meta || {}) }), [data]); // eslint-disable-line react-hooks/exhaustive-deps
  const metrics = data.metrics || {};
  const trades  = data.trades  || [];

  // ── Рядки для таблиці періодів ────────────────────────────────────────────
  // Поверх збережених результатів кладемо periodOverrides — інакше щойно
  // перерахований період показував би в таблиці старі числа.
  // `_idx` — індекс у ПОВНОМУ periods_results; саме він адресує перерахунок,
  // і саме його треба нести окремо, бо позиція в таблиці може зсунутись
  // (сортування, синтетичний головний рядок для старих прогонів).
  const mainRowPos = periods.findIndex(p => p && p.is_main);
  // ── Блоки дописаних вікон: прикріпити / відкріпити ──────────────────────
  // Дописані вікна зсувають середні рядка, і він перестає бути порівнянним із
  // сусідами в таблиці, у яких їх немає. Тому блок вмикається в підсумок
  // свідомо, а не самим фактом розрахунку (рішення менеджера 07.09).
  const [blockBusy, setBlockBusy] = useState(null);
  const [blockErr,  setBlockErr]  = useState('');
  useEffect(() => { setBlockErr(''); }, [data?.id]);
  const periodBlocks = useMemo(() => {
    const out = [];
    const seen = new Map();
    effectivePeriods.forEach(p => {
      if (!p || !p.block) return;
      if (!seen.has(p.block)) {
        seen.set(p.block, out.length);
        out.push({ id: p.block, label: p.block_label || p.block,
                   included: p.included !== false, n: 0 });
      }
      out[seen.get(p.block)].n += 1;
    });
    return out;
  }, [effectivePeriods]);

  // «+» на рядку ГОЛОВНОЇ таблиці періодів. Той самий початок, вікно довше на N
  // місяців — щоб побачити, чи стає збитковий рік прибутковим на двох-трьох.
  const [extendBusy, setExtendBusy] = useState(null);

  const extendMainPeriod = useCallback(async (period, pos, months) => {
    const rowId = data?.id;
    if (!rowId || !period?.start) return;
    if (!months || months < 1) { setBlockErr('Некоректна кількість місяців'); return; }
    setExtendBusy(pos); setBlockErr('');
    try {
      const res = await extendPeriod(rowId, period.start, months);
      if (Array.isArray(res.data?.periods_results)) {
        setPeriodsPatch(res.data.periods_results);
      }
    } catch (e) {
      setBlockErr(e?.response?.data?.detail || e.message || 'Не вдалось продовжити період');
    } finally {
      setExtendBusy(null);
    }
  }, [data?.id]);

  // ── Драбина варіантів ────────────────────────────────────────────────────
  // Заміщає дві зашиті драбини (капітал 50/100/200 тис. і «плече × обіг»).
  // Різниця не косметична: старі рахували ОДНУ симуляцію на щабель — головне
  // вікно, — тож казали «скільки вийде», але не «як часто буває погано».
  // Кожна вісь має власний перемикач і власний діапазон: увімкнені рахуються
  // ОДНИМ запуском, але дають ОКРЕМІ драбини (не перетин — див. коментар до
  // VariantRunBody на бекенді).
  // Вибір осей памʼятається в браузері — один на ВСІ рядки, а не на рядок:
  // драбину зазвичай ганяють тими самими діапазонами по черзі на кількох
  // стратегіях. Збережене зливається з дефолтом по кожній осі, тож нова вісь
  // (або нове поле осі) у старому записі не ламає форму.
  const [varAxesRaw, setVarAxesRaw] = useLocalStorage('bd_varAxes', VAR_AXES_DEFAULT);
  const varAxes = useMemo(() => mergeVarAxes(varAxesRaw), [varAxesRaw]);
  const setVarAxes = useCallback(
    (fn) => setVarAxesRaw(prev => (typeof fn === 'function' ? fn(mergeVarAxes(prev)) : fn)),
    [setVarAxesRaw]);
  const [varWindows, setVarWindows] = useLocalStorage('bd_varWindows', 'base');
  // Тікери, яких для прогону НЕ ІСНУЄ. Окреме поле, а не вісь: це не діапазон.
  const [varExclude, setVarExclude] = useState('');
  const [varBusy,    setVarBusy]    = useState(false);
  const [varMsg,     setVarMsg]     = useState('');
  const [varErr,     setVarErr]     = useState('');
  const [varOpen,    setVarOpen]    = useState(null);   // розгорнутий запуск
  // Пачка, що зараз рахується: щаблі показуються по мірі готовності. До цього
  // користувач бачив лише «запущено» й порожнечу — не відрізнити «рахується»
  // від «загубилось» (а воно й губилось: воркер із старим кодом стирав пачку).
  const [varLive, setVarLive] = useState(null);   // {batchId, done, total, ladders}
  const varPollRef = useRef(null);

  const [varRunsPatch, setVarRunsPatch] = useState(null);
  useEffect(() => { setVarRunsPatch(null); }, [data?.id]);
  const variantRuns = useMemo(
    () => varRunsPatch || (Array.isArray(data?.variant_runs) ? data.variant_runs : []),
    [varRunsPatch, data?.variant_runs]);

  // Скільки щаблів вийде — рахуємо тим самим правилом, що й бекенд, щоб
  // користувач бачив число ДО запуску, а не отримував відмову після.
  const varSteps = useMemo(() => {
    // Сума щаблів увімкнених осей — саме СУМА, бо осі незалежні. Виключення без
    // жодної осі — теж повноцінний прогін, один щабель «без REPL».
    const onlyExclude = Object.values(varAxes).every(r => !r.on);
    if (onlyExclude) return varExclude.trim() ? 1 : 0;
    return Object.values(varAxes).reduce((acc, r) => {
      if (!r.on) return acc;
      const a = Number(r.from), b = Number(r.to), st = Number(r.step);
      if (!(st > 0) || !(b >= a)) return acc;
      const n = Math.round((b - a) / st) + 1;
      return acc + n + (r.withOff && a > 0 ? 1 : 0);
    }, 0);
  }, [varAxes, varExclude]);

  const varWindowCount = useMemo(() => {
    const list = Array.isArray(data?.periods_results) ? data.periods_results : [];
    return list.filter(p => p && !p.is_main && !p.error
                            && (varWindows === 'all' || !p.block)).length;
  }, [data?.periods_results, varWindows]);

  const pollVariants = useCallback((rowId, batchId) => {
    if (varPollRef.current) clearInterval(varPollRef.current);
    const tick = async () => {
      try {
        const res = await getVariantProgress(rowId, batchId);
        const d = res.data || {};
        setVarLive({ batchId, done: d.done || 0, total: d.total || 0,
                     wDone: d.windows_done, wTotal: d.windows_total,
                     ladders: d.ladders || [] });
        if (d.status && d.status !== 'running' && d.status !== 'queued') {
          clearInterval(varPollRef.current); varPollRef.current = null;
          // Пачка злилась у рядок — забираємо готові драбини й ховаємо живу.
          try {
            const fresh = await getAnalyzerResult(rowId);
            if (Array.isArray(fresh.data?.variant_runs)) {
              setVarRunsPatch(fresh.data.variant_runs);
            }
          } catch (_) {}
          setVarLive(null);
        }
      } catch (e) {
        // 404 = бекенд ще без цього ендпоінта. Не привід зупиняти опитування
        // прогресу: лічильник нижче все одно показує, скільки пораховано.
        if (e?.response?.status && e.response.status !== 404) {
          clearInterval(varPollRef.current); varPollRef.current = null;
        }
      }
    };
    // Перше опитування одразу: інакше таблиця з рядками «рахується…» після
    // запуску чи перезаходу на сторінку зʼявлялась би лише через 5 секунд.
    varPollRef.current = setInterval(tick, 5000);
    tick();
  }, []);

  useEffect(() => () => { if (varPollRef.current) clearInterval(varPollRef.current); }, []);

  // Драбина могла бути запущена до перезавантаження сторінки або з іншого
  // компʼютера — тоді номер пачки знає лише сервер. Підхоплюємо її сюди.
  useEffect(() => {
    const rowId = data?.id;
    // У кабінеті користувача (embedded) рядка аналізатора немає — id там
    // штучний ("empty"), і запит лише давав 422 у консолі.
    if (!rowId || embedded) return;
    let alive = true;
    getActiveVariant(rowId).then(res => {
      const bid = res?.data?.batch_id;
      if (alive && bid && !varPollRef.current) {
        setVarLive({ batchId: bid, done: 0, total: 0, ladders: [] });
        pollVariants(rowId, bid);
      }
    }).catch(() => {});
    return () => { alive = false; };
  }, [data?.id, embedded, pollVariants]);

  const runVariants = useCallback(async () => {
    const rowId = data?.id;
    if (!rowId) return;
    setVarBusy(true); setVarErr(''); setVarMsg('');
    try {
      const axes = Object.entries(varAxes)
        .filter(([, r]) => r.on)
        .map(([axis, r]) => ({ axis, v_from: Number(r.from), v_to: Number(r.to),
                               v_step: Number(r.step),
                               ...(axis === 'hold_to_cost' ? { with_off: !!r.withOff } : {}) }));
      const exclude_tickers = varExclude.split(/[\s,;]+/).filter(Boolean);
      const res = await runVariantLadder(rowId, { axes, exclude_tickers, windows: varWindows });
      const d = res.data || {};
      setVarMsg(`Рахується: ${(d.axes || []).length} драбин, ${d.steps} щаблів `
              + `на ${d.windows} вікнах. Щаблі зʼявляться нижче по мірі готовності.`);
      if (d.batch_id) {
        setVarLive({ batchId: d.batch_id, done: 0, total: d.steps || 0, ladders: [] });
        pollVariants(rowId, d.batch_id);
      }
    } catch (e) {
      setVarErr(e?.response?.data?.detail || e.message || 'Не вдалось запустити');
    } finally {
      setVarBusy(false);
    }
  }, [data?.id, varAxes, varWindows, varExclude, pollVariants]);

  const toggleBlock = useCallback(async (blockId, included) => {
    const rowId = data?.id;
    if (!rowId) return;
    setBlockBusy(blockId); setBlockErr('');
    try {
      const res = await togglePeriodBlock(rowId, blockId, included);
      if (Array.isArray(res.data?.periods_results)) {
        setPeriodsPatch(res.data.periods_results);
      }
    } catch (e) {
      setBlockErr(e?.response?.data?.detail || e.message || 'Не вдалось перемкнути блок');
    } finally {
      setBlockBusy(null);
    }
  }, [data?.id]);

  const periodsForTable = (() => {
    const rows = periods.map((p, idx) => ({ ...p, ...(periodOverrides[idx] || {}), _idx: idx }));
    if (mainRowPos >= 0) return rows;
    // Старі прогони (до рівняння періодів) головного рядка не мають — збираємо
    // його з метрик самого результату, щоб таблиця не втратила «★».
    const _s = params.start_time || metrics.start_time || '';
    const _e = params.stop_time  || metrics.stop_time  || '';
    const _days = (() => {
      // periodDays нижче за текстом — тут рахуємо самі, інакше TDZ.
      try {
        const d = Math.round((new Date(_e) - new Date(_s)) / 86400000);
        return d > 0 ? d : null;
      } catch (_) { return null; }
    })();
    const mainRow = {
      is_main: true, _idx: -1,
      start: _s,
      end:   _e,
      days:  _days,
      monthly_return: metrics.monthly_return, yearly_return: metrics.yearly_return,
      win_rate: metrics.win_rate, buys: data.buy_count, sells: data.sell_count,
      total_return: metrics.total_return, final_balance: metrics.final_balance,
      max_drawdown: metrics.max_drawdown, sharpe: metrics.sharpe_ratio,
      calmar: metrics.calmar_ratio, profitable_months_pct: metrics.profitable_months_pct,
      worst_month: metrics.worst_month,
      total_commission: data.commission_usd, total_spread: data.spread_usd,
    };
    return [mainRow, ...rows];
  })();
  // Підсвічуємо рядок за ПОЗИЦІЄЮ в таблиці, а не за _idx.
  const activeRowPos = activeTab === 0
    ? periodsForTable.findIndex(r => r.is_main)
    : periodsForTable.findIndex(r => r._idx === extraPeriods[activeTab - 1]?.idx);
  // Дати життя тікерів: рахуються бекендом на читанні, тому є і в старих
  // збережених прогонах. Сенс — показати, що реєстр справді широкий:
  // у ньому є і новачки, і папери, що зникли з біржі.
  const tickerLife = data.ticker_life || {};
  const lifeSummary = data.ticker_life_summary || null;
  // Кабінет бота дат життя не віддає — там три колонки були суцільними
  // прочерками (менеджер 16.10). Показуємо їх лише коли дані є.
  const hasTickerLife = Object.keys(tickerLife).length > 0;
  // Колонка «Volatility» у кабінеті бота (app.*) не потрібна (менеджер 03.11).
  const showVolatilityCol = process.env.REACT_APP_VARIANT !== 'trading';

  // Ярлик життя паперу. ⚠️ «Дані з» — це НЕ дата IPO: для більшості компаній
  // вона впирається в межу нашого сховища (перший бар AAPL — 2020-01-02, хоча
  // Apple торгується з 1980). Тому три різні стани, а не «новачок / старий».
  const lifeBadge = (s) => {
    const pill = (bg, color, text, title) => (
      <span title={title} style={{
        display: 'inline-block', padding: '1px 7px', borderRadius: 10, fontSize: 11,
        background: bg, color, whiteSpace: 'nowrap',
      }}>{text}</span>
    );
    if (s.delisting) {
      const d = s.delisting.date || '';
      const ty = s.delisting.type ? ` · ${s.delisting.type}` : '';
      return pill('#FEE2E2', '#B91C1C', `🪦 зник ${d}`,
        `Компанія покинула біржу${ty}. Джерело: ${s.delisting.source === 'events'
          ? 'реєстр подій (є бари)' : 'форма SEC 25'}`);
    }
    if (s.dataEndsEarly) {
      return pill('#FEF3C7', '#92400E', 'дані обриваються',
        'Барів немає більш ніж місяць до кінця сховища, а події делістингу в реєстрі нема — '
        + 'або папір помер і ми цього не зафіксували, або діра в даних.');
    }
    if (s.origin === 'listed') {
      return pill('#DCFCE7', '#15803D', 'новачок',
        'Перший бар більш ніж за 90 днів після межі сховища — компанія справді '
        + 'з\'явилась на ринку в межах наших даних.');
    }
    if (s.origin === 'store_floor') {
      // Це стан БІЛЬШОСТІ паперів (2912 із 4632), тож ярлик мусить читатись як
      // довідка, а не як дефект: «історія обрізана» тривожила там, де все гаразд.
      return pill('#F1F5F9', '#475569', 'торгувався й раніше',
        'Перший бар упирається в межу нашого сховища: компанія існувала й раніше, '
        + 'ми просто не маємо тих котирувань. Це НЕ дата виходу на біржу.');
    }
    if (s.origin === 'uncertain') {
      return pill('#F1F5F9', '#475569', 'невизначено',
        'Перший бар у межах 90 днів після межі сховища — сказати, це лістинг чи '
        + 'просто перший торгований бар тонкого паперу, неможливо.');
    }
    if (s.origin === 'no_bars') {
      return pill('#F1F5F9', '#475569', 'барів немає', 'Котирувань у сховищі немає взагалі.');
    }
    return <span style={{ color: C.textSm }}>—</span>;
  };
  const equity  = data.equity_curve || [];
  const initialCapital = metrics.starting_capital || params.initial_capital || 100000;

  // Data-driven column visibility — determined by what fields the trades actually
  // carry. Allows BacktestDetail to be reused in read-only / detail-gated contexts
  // (e.g. virtual trading) without adding a separate prop: the backend already
  // strips fields it doesn't want the user to see.
  const hasTicker  = trades.some(t => t.ticker  != null);
  const hasDetails = trades.some(t => t.description != null);

  // ── Shared: build ticker navigate context from ticker symbol ─────────────
  const navigateByTicker = (ticker) => {
    if (!onTickerClick) return;
    // Anchor the loaded window to the FULL backtest period (not the trades):
    // left = period start − pre-history (strategy lookback, min half a year),
    // right = period end. The "Угода" preset still zooms to the trade bracket
    // within this loaded window. Same helper as the direct-URL path.
    onTickerClick(buildTickerTradeContext({
      ticker,
      trades:     trades.filter(t => t.ticker === ticker),
      params,
      meta:       data._meta || {},
      warmupBars: data._meta?.warmup_bars ?? 0,
    }));
  };

  // ── Ticker click → navigate to chart ─────────────────────────────────────
  const handleTickerClick = (trade) => {
    if (!onTickerClick) return;
    navigateByTicker(trade.ticker);
  };

  // ── Fetch market volatility when overlay is toggled ───────────────────────
  useEffect(() => {
    if (!volOn) { setVolData([]); return; }
    if (!equity.length) return;
    setVolLoading(true);
    const from = equity[0].date.slice(0, 10);
    const to   = equity[equity.length - 1].date.slice(0, 10);
    getMarketVolatility({ type: 'daily', from, to, threshold: 1.0 })
      .then(res => setVolData(res.data))
      .catch(() => setVolData([]))
      .finally(() => setVolLoading(false));
  }, [volOn]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Anomaly detection in equity curve ────────────────────────────────────
  // 🚨 АНОМАЛІЯ — ЦЕ НЕМОЖЛИВЕ ЧИСЛО, А НЕ МАЛИЙ БАЛАНС.
  // Тут стояло `p.balance <= initialCapital * 0.01`, і такі точки викидались із
  // графіка. Наслідок: щойно стратегія втрачала 99%, крива переставала показувати
  // саме ту ділянку, що робить її катастрофічною.
  // Виміряно на #7211561 (2022, старт $100k): 1196 → 1358 → 1576 → 1049 → 798 →
  // … → 345, плавно, без розривів і NaN. 28 днів справжніх даних зникали з
  // графіка, і рахунок виглядав живішим, ніж був насправді.
  // Падіння до нуля — це результат, який треба ПОКАЗАТИ, а не сховати.
  const equityAnomalies = useMemo(() => {
    if (data.equity_anomalies && data.equity_anomalies.length > 0) return data.equity_anomalies;
    return equity.filter(p => !Number.isFinite(p.balance));
  }, [equity, data.equity_anomalies]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Clean equity curve: filter anomalous points for chart ─────────────────
  const cleanEquity = useMemo(() => {
    if (!equityAnomalies.length) return equity;
    const anomalyDates = new Set(equityAnomalies.map(p => p.date));
    return equity.filter(p => !anomalyDates.has(p.date));
  }, [equity, equityAnomalies]);

  // ── Equity curve filtering by range ───────────────────────────────────────
  const filteredEquity = useMemo(() => {
    const base = cleanEquity;
    if (!base.length || rangeFilter === 'All') return base;
    const last = new Date(base[base.length - 1].date);
    let cutoff;
    if (rangeFilter === '7D')  { cutoff = new Date(last); cutoff.setDate(last.getDate() - 7); }
    if (rangeFilter === 'MTD') { cutoff = new Date(last.getFullYear(), last.getMonth(), 1); }
    if (rangeFilter === 'YTD') { cutoff = new Date(last.getFullYear(), 0, 1); }
    if (rangeFilter === '1Y')  { cutoff = new Date(last); cutoff.setFullYear(last.getFullYear() - 1); }
    return base.filter(e => new Date(e.date) >= cutoff);
  }, [cleanEquity, rangeFilter]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Chart data (value or performance %) ───────────────────────────────────
  const chartData = useMemo(() => {
    if (!filteredEquity.length) return [];
    const base = filteredEquity[0].balance;
    const n = filteredEquity.length;

    // Лінійна апроксимація (trend line) по балансу
    const sumX = n * (n - 1) / 2;
    const sumX2 = n * (n - 1) * (2 * n - 1) / 6;
    const sumY  = filteredEquity.reduce((s, e) => s + e.balance, 0);
    const sumXY = filteredEquity.reduce((s, e, i) => s + i * e.balance, 0);
    const denom = n * sumX2 - sumX * sumX;
    const slope  = denom ? (n * sumXY - sumX * sumY) / denom : 0;
    const intercept = (sumY - slope * sumX) / n;

    return filteredEquity.map((e, i) => ({
      date:    e.date,
      balance: e.balance,
      value:   parseFloat(e.balance.toFixed(2)),
      perf:    parseFloat(((e.balance / base - 1) * 100).toFixed(3)),
      trend:   parseFloat((intercept + slope * i).toFixed(2)),
      trendPerf: parseFloat((((intercept + slope * i) / base - 1) * 100).toFixed(3)),
    }));
  }, [filteredEquity]);

  // ── Merged chart data with volatility overlay ─────────────────────────────
  const mergedChartData = useMemo(() => {
    if (!volOn || !volData.length) return chartData;
    const volMap = new Map(volData.map(v => [v.date, { vi: v.volatility_index, am: v.avg_move }]));
    return chartData.map(d => {
      const v = volMap.get(d.date.slice(0, 10));
      return { ...d, vol_index: v?.vi ?? null, avg_move: v?.am ?? null };
    });
  }, [chartData, volOn, volData]);

  // ── Pearson correlation: equity returns vs market avg_move ────────────────
  const volCorrelation = useMemo(() => {
    if (!volOn || !volData.length || mergedChartData.length < 3) return null;
    const xs = [], ys = [];
    for (let i = 1; i < mergedChartData.length; i++) {
      const prev = mergedChartData[i - 1].balance;
      const curr = mergedChartData[i].balance;
      if (!prev || !curr) continue;
      const ret = (curr / prev - 1) * 100;
      const am  = mergedChartData[i].avg_move;
      if (am != null && isFinite(ret)) { xs.push(ret); ys.push(am); }
    }
    return pearsonCorr(xs, ys);
  }, [volOn, mergedChartData]);

  // Y-axis domain for chart
  const chartField = chartMode === 'value' ? 'value' : 'perf';
  const chartValues = chartData.map(d => d[chartField]);
  const yMin = chartValues.length ? Math.min(...chartValues) : 0;
  const yMax = chartValues.length ? Math.max(...chartValues) : 1;
  const yPad = (yMax - yMin) * 0.05 || 0.5;

  // Custom Y-axis ticks: evenly spaced at multiples of initialCapital fractions
  const yTicksValue = useMemo(() => {
    if (chartMode !== 'value' || !chartValues.length || !initialCapital) return undefined;
    const lo = 0;
    const hi = yMax + yPad;
    const range = hi - lo;
    if (range <= 0) return undefined;
    // Candidate step sizes as fractions of initialCapital (produces "round" tick labels)
    const fracs = [0.001, 0.002, 0.005, 0.01, 0.02, 0.05, 0.1, 0.2, 0.25, 0.5, 1];
    const rawStep = range / 7;
    const step = fracs
      .map(f => initialCapital * f)
      .filter(c => c > 0)
      .reduce((best, c) => Math.abs(c - rawStep) < Math.abs(best - rawStep) ? c : best);
    const startM = Math.ceil(lo / step);
    const endM   = Math.floor(hi / step);
    const ticks = [];
    for (let m = startM; m <= endM; m++) ticks.push(Math.round(m * step * 100) / 100);
    return ticks.length >= 2 ? ticks : undefined;
  }, [chartMode, chartValues, yMin, yMax, yPad, initialCapital]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Trades filtering ──────────────────────────────────────────────────────
  const filteredTrades = useMemo(() => {
    let result = trades;
    if (tradeFilter !== 'ALL') result = result.filter(t => t.action === tradeFilter);
    if (tickerFilter) result = result.filter(t => t.ticker === tickerFilter);
    return result;
  }, [trades, tradeFilter, tickerFilter]);

  const isBuyRow = (t) => t.action === 'BUY';

  // ── Sort handler ─────────────────────────────────────────────────────────
  const handleSort = (col) => {
    if (sortCol === col) {
      setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    } else {
      setSortCol(col);
      setSortDir('asc');
    }
    setTradePage(0);
  };

  // ── Sorted trades ─────────────────────────────────────────────────────────
  const sortedTrades = useMemo(() => {
    return [...filteredTrades].sort((a, b) => {
      let valA = a[sortCol];
      let valB = b[sortCol];
      if (['id', 'qty', 'price', 'sum', 'change_usd', 'change_pct', 'start_sum', 'final_sum'].includes(sortCol)) {
        const get = sortCol === 'start_sum' ? startSum
                  : sortCol === 'final_sum' ? finalSum
                  : (t) => t[sortCol];
        valA = parseFloat(get(a)) || 0;
        valB = parseFloat(get(b)) || 0;
        return sortDir === 'asc' ? valA - valB : valB - valA;
      }
      if (sortCol === 'time') {
        valA = new Date(valA).getTime();
        valB = new Date(valB).getTime();
        return sortDir === 'asc' ? valA - valB : valB - valA;
      }
      if (sortCol === 'duration') {
        const toMin = (v) => {
          if (!v || v === 'open') return Infinity;
          const days  = (v.match(/(\d+)д/) || [0, 0])[1];
          const hours = (v.match(/(\d+)г/) || [0, 0])[1];
          const mins  = (v.match(/(\d+)хв/) || [0, 0])[1];
          return (+days * 1440) + (+hours * 60) + (+mins);
        };
        valA = toMin(isBuyRow(a) ? 'open' : fmtDuration(a.time, a.buy_time));
        valB = toMin(isBuyRow(b) ? 'open' : fmtDuration(b.time, b.buy_time));
        return sortDir === 'asc' ? valA - valB : valB - valA;
      }
      valA = String(valA || '').toLowerCase();
      valB = String(valB || '').toLowerCase();
      if (valA < valB) return sortDir === 'asc' ? -1 : 1;
      if (valA > valB) return sortDir === 'asc' ?  1 : -1;
      return 0;
    });
  }, [filteredTrades, sortCol, sortDir]); // eslint-disable-line react-hooks/exhaustive-deps

  const handlePageSize = (val) => {
    setPageSize(val === 'all' ? 'all' : +val);
    setTradePage(0);
  };

  // ── Equity curve click → jump to nearest trade ────────────────────────────
  const handleChartClick = useCallback((chartState) => {
    if (!chartState || !chartState.activePayload || !chartState.activePayload.length) return;
    const clickedDate = chartState.activePayload[0]?.payload?.date;
    if (!clickedDate) return;
    const clickedMs = new Date(clickedDate).getTime();
    let bestIdx = 0;
    let bestDiff = Infinity;
    sortedTrades.forEach((t, i) => {
      const tMs = new Date(t.time).getTime();
      const diff = Math.abs(tMs - clickedMs);
      if (diff < bestDiff) { bestDiff = diff; bestIdx = i; }
    });
    if (pageSize !== 'all') {
      const ps = typeof pageSize === 'number' ? pageSize : 50;
      setTradePage(Math.floor(bestIdx / ps));
    }
    setHighlightedTradeIdx(bestIdx);
    if (tradesCardRef.current) {
      tradesCardRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [sortedTrades, pageSize]);

  // scroll highlighted row into view after page change
  useEffect(() => {
    if (highlightedTradeIdx === null) return;
    const timer = setTimeout(() => {
      if (highlightedRowRef.current) {
        highlightedRowRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }, 120);
    return () => clearTimeout(timer);
  }, [highlightedTradeIdx, tradePage]);

  const displayedTrades = pageSize === 'all'
    ? sortedTrades
    : sortedTrades.slice(tradePage * pageSize, (tradePage + 1) * pageSize);

  const totalTradePages = pageSize === 'all' ? 1 : Math.ceil(sortedTrades.length / pageSize);

  const paginBtn = (disabled) => ({
    padding: '3px 8px', borderRadius: 5, border: `1px solid ${C.border}`,
    background: 'transparent', color: disabled ? C.textSm : C.text,
    cursor: disabled ? 'default' : 'pointer', fontSize: 12, opacity: disabled ? 0.4 : 1,
  });

  // ── Ticker statistics ─────────────────────────────────────────────────────
  const tickerStats = useMemo(() => {
    const map = {};
    for (const t of trades) {
      if (!map[t.ticker]) {
        map[t.ticker] = { ticker: t.ticker, buys: 0, sells: 0, wins: 0, prices: [], pnls: [] };
      }
      const s = map[t.ticker];
      if (t.action === 'BUY') s.buys++;
      else {
        s.sells++;
        if (t.pnl_pct != null && t.pnl_pct > 0) s.wins++;
        if (t.pnl_pct != null) s.pnls.push(t.pnl_pct);
      }
      if (t.price != null) s.prices.push(t.price);
    }
    return Object.values(map).map(s => {
      const total    = s.buys + s.sells;
      const winRate  = s.sells > 0 ? (s.wins / s.sells * 100) : null;
      const avgPnl   = s.pnls.length ? s.pnls.reduce((a, b) => a + b, 0) / s.pnls.length : null;
      let volatility = null;
      if (s.prices.length >= 2) {
        const minP = Math.min(...s.prices);
        const maxP = Math.max(...s.prices);
        const avgP = s.prices.reduce((a, b) => a + b, 0) / s.prices.length;
        volatility = avgP > 0 ? (maxP - minP) / avgP * 100 : null;
      }
      const life = tickerLife[s.ticker] || {};
      return {
        ticker: s.ticker, total, buys: s.buys, sells: s.sells, winRate, avgPnl, volatility,
        firstBar:      life.first_bar || null,
        lastBar:       life.last_bar  || null,
        origin:        life.origin    || null,
        delisting:     life.delisting || null,
        dataEndsEarly: !!life.data_ends_early,
      };
    });
  }, [trades, tickerLife]); // eslint-disable-line react-hooks/exhaustive-deps

  const sortedTickerStats = useMemo(() => {
    return [...tickerStats].sort((a, b) => {
      const va = a[tickerSortCol] ?? -Infinity;
      const vb = b[tickerSortCol] ?? -Infinity;
      if (typeof va === 'string') {
        return tickerSortDir === 'asc' ? va.localeCompare(vb) : vb.localeCompare(va);
      }
      return tickerSortDir === 'asc' ? va - vb : vb - va;
    });
  }, [tickerStats, tickerSortCol, tickerSortDir]);

  const handleTickerSort = (col) => {
    if (tickerSortCol === col) setTickerSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setTickerSortCol(col); setTickerSortDir('desc'); }
  };

  // ── Params summary string ─────────────────────────────────────────────────
  const _rp  = params.risk_protection || {};
  const _meta = data._meta || {};
  const paramsSummary = [
    params.start_time && `${params.start_time}–${params.stop_time}`,
    params.data_type && params.data_type,
    params.x_buy && `rise ${params.x_buy}% / ${fmtWin(params.t_hours_buy ?? params.buy_window_days, params.data_type)}`,
    params.y_sell && `drop ${params.y_sell}% / ${fmtWin(params.sell_window_days, params.data_type)}`,
    params.num_companies && `${params.num_companies} cos.`,
    // Слот просунутого рушія — ПОХІДНЕ (капітал ÷ N), а не окремий параметр:
    // `simulate_advanced` поля `pos_size` не приймає взагалі. У простих рушіях
    // (base/momentum/MR) воно діє — там показуємо як було.
    (String(params.strategy || '') === 'advanced'
      ? (Number(params.num_companies) > 0 && `слот = капітал ÷ ${params.num_companies}`)
      : (params.pos_size != null && `pos ${params.pos_size}%`)),
    params.initial_capital && `$${Number(params.initial_capital).toLocaleString()}`,
    params.hard_stop_pct > 0 && `HS ${params.hard_stop_pct}%`,
    params.hard_stop_cooldown_bars > 0 && `CD ${params.hard_stop_cooldown_bars}б`,
    params.sell_mode && params.sell_mode !== 'rolling' && `mode: ${params.sell_mode}`,
    _rp.sell_overnight && 'overnight',
    _rp.sell_weekend && 'weekend',
    _rp.max_loss_to_sell_pct > 0 && `ML ${_rp.max_loss_to_sell_pct}%`,
    params.stagnation_enabled && `stag [${params.stagnation_min_pct ?? -0.5}%..${params.stagnation_max_pct ?? 0.5}%] ${params.stagnation_bars ?? 30}б`,
    params.intraday_enabled && `intraday ${params.intraday_pct ?? ''}%`,
    params.reinvest && params.reinvest !== true && params.reinvest !== 'true' && `reinvest: ${params.reinvest}`,
    (params.use_peak_window_buy || params.use_peak_window_sell) && 'peak window',
    // meta fields
    _meta.specific_tickers?.length > 0 && `tickers: ${_meta.specific_tickers.join(', ')}`,
    _meta.sectors?.length > 0 && !(_meta.sectors.length === 1 && _meta.sectors[0] === 'Technology') && `sector: ${_meta.sectors.join('+')}`,
    _meta.countries?.length > 0 && !(_meta.countries.length === 1 && _meta.countries[0] === 'USA') && `country: ${_meta.countries.join('+')}`,
    _meta.indexes?.length > 0 && `index: ${_meta.indexes.join('+')}`,
    _meta.market_cap_type && _meta.market_cap_type !== 'custom' && `cap: ${_meta.market_cap_type}`,
    _meta.market_cap_type === 'custom' && _meta.market_cap_min != null && `cap: ${(_meta.market_cap_min/1e9).toFixed(1)}B–${_meta.market_cap_max >= 1e14 ? '∞' : (_meta.market_cap_max/1e9).toFixed(0) + 'B'}`,
    (_meta.beta_min != null || _meta.beta_max != null) && `β ${_meta.beta_min ?? ''}–${_meta.beta_max ?? ''}`,
    _meta.min_avg_volume > 0 && `vol≥${(_meta.min_avg_volume/1e6).toFixed(1)}M`,
    _meta.regime_filter?.length > 0 && `regime: ${_meta.regime_filter.join('+')}`,
  ].filter(Boolean).join('  ·  ');

  // ── Period calculation ────────────────────────────────────────────────────
  const periodStr = (() => {
    try {
      const startStr = metrics.start_time || params.start_time || (equity.length ? equity[0].date : null);
      const endStr   = metrics.stop_time  || params.stop_time  || (equity.length ? equity[equity.length - 1].date : null);
      if (!startStr || !endStr) return null;
      const days   = Math.round((new Date(endStr) - new Date(startStr)) / 86400000);
      if (days <= 0) return null;
      const years  = Math.floor(days / 365);
      const months = Math.floor((days % 365) / 30);
      const remDays = (days % 365) % 30;
      const parts = [];
      if (years  > 0) parts.push(`${years} year${years  > 1 ? 's' : ''}`);
      if (months > 0) parts.push(`${months} month${months > 1 ? 's' : ''}`);
      if (remDays > 0) parts.push(`${remDays} day${remDays > 1 ? 's' : ''}`);
      if (!parts.length) return `${days} days`;
      if (parts.length === 1) return parts[0];
      return parts[0] + '\n' + parts.slice(1).join(' ');
    } catch (_) { return null; }
  })();

  // Той самий період українською з правильною формою числа («4 дні», «5 днів»)
  // — для кабінету користувача (embedded).
  const periodStrUk = (() => {
    const s0 = metrics.start_time || params.start_time || (equity.length ? equity[0].date : null);
    const e0 = metrics.stop_time  || params.stop_time  || (equity.length ? equity[equity.length - 1].date : null);
    if (!s0 || !e0) return null;
    const days = Math.round((new Date(e0) - new Date(s0)) / 86400000);
    if (!(days > 0)) return null;
    const plural = (n, one, few, many) => {
      const m10 = n % 10, m100 = n % 100;
      if (m10 === 1 && m100 !== 11) return one;
      if (m10 >= 2 && m10 <= 4 && (m100 < 12 || m100 > 14)) return few;
      return many;
    };
    const years = Math.floor(days / 365);
    const months = Math.floor((days % 365) / 30);
    const rem = (days % 365) % 30;
    const parts = [];
    if (years  > 0) parts.push(`${years} ${plural(years, 'рік', 'роки', 'років')}`);
    if (months > 0) parts.push(`${months} ${plural(months, 'місяць', 'місяці', 'місяців')}`);
    if (rem    > 0) parts.push(`${rem} ${plural(rem, 'день', 'дні', 'днів')}`);
    return parts.join(' ');
  })();

  // Calendar-day span of the result — used to hide annualized returns (Monthly/
  // Yearly) on very short windows in the embedded (bot) view, where extrapolating
  // a 1-day return to a year gives meaningless numbers (e.g. +53,000,000%).
  const periodDays = (() => {
    try {
      const s = metrics.start_time || params.start_time || (equity.length ? equity[0].date : null);
      const e = metrics.stop_time  || params.stop_time  || (equity.length ? equity[equity.length - 1].date : null);
      if (!s || !e) return null;
      const d = Math.round((new Date(e) - new Date(s)) / 86400000);
      return d > 0 ? d : null;
    } catch (_) { return null; }
  })();

  // Кабінет (менеджер 20.10): дохідність замість дублю сум зверху.
  // Річна = (1+r)^(365/днів) − 1, але лише для періоду ≥ 365 днів: коротше
  // вікно, розтягнуте на рік, дає фантазію (−0.85% за 4 дні → −54%), тож там
  // стоїть фактична дохідність за період — з «річних», як на макеті менеджера.
  const cabinetReturns = (() => {
    const fmt = (r, annual) => r == null || !isFinite(r) ? null
      : { value: fmtPct(r * 100) + (annual ? ' річних' : ''), color: r >= 0 ? C.success : C.danger };
    const s0 = Number(metrics.starting_capital), f0 = Number(metrics.final_balance);
    const total = s0 > 0 && isFinite(f0) ? f0 / s0 - 1 : null;
    if (total == null) return { all: null, last365: null };
    if (!(periodDays >= 365)) {
      const v = fmt(total, true);
      return { all: v, last365: v };
    }
    const all = fmt(Math.pow(1 + total, 365 / periodDays) - 1, true);
    // Останні 365 днів — з кривої капіталу: точка, найближча до (кінець − 365 дн).
    let last365 = null;
    const pts = equity.filter(p => p && p.date && Number.isFinite(p.balance));
    if (pts.length >= 2) {
      const endT = new Date(pts[pts.length - 1].date).getTime();
      const cut = endT - 365 * 86400000;
      let base = pts[0];
      for (const p of pts) { if (new Date(p.date).getTime() <= cut) base = p; else break; }
      const endB = pts[pts.length - 1].balance;
      if (base.balance > 0) last365 = fmt(endB / base.balance - 1, true);
    }
    return { all, last365 };
  })();

  // ── Tick formatter ────────────────────────────────────────────────────────
  const tickFmt = (v) => {
    if (chartMode === 'value') {
      if (v >= 1_000_000) return (v / 1_000_000).toFixed(1) + 'M $';
      if (v >= 1_000)     return (v / 1_000).toFixed(0) + 'k $';
      return v + ' $';
    }
    return v.toFixed(1) + '%';
  };

  return (
    <div style={embedded ? { color: C.text, fontFamily: "'Inter','Segoe UI',system-ui,sans-serif" } : S.page}>
      <div style={embedded ? {} : S.wrap}>

        {/* ── Періоди: таблиця замість кнопок-пігулок ──────────────────────────
            Кнопки несли лише дати, тож при десятку періодів вибрати було ні за
            чим — доводилось заходити в кожен. Тепер це та сама таблиця, що в
            попапі зі списку результатів (спільний PeriodsTable), а клік по
            рядку робить рівно те, що робила пігулка: відкриває період.
            Рядок ★ (головний) = колишня кнопка «Main». */}
        {extraPeriods.length > 0 && (
          <div style={{ ...S.card, marginBottom: 16, padding: 12 }}>
            <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, flexWrap: 'wrap' }}>
              <div style={{ fontSize: 13, fontWeight: 700, color: C.textLg }}>
                Періоди ({extraPeriods.length + 1})
              </div>
              <div style={{ fontSize: 11, color: C.textSm }}>
                Клік по рядку відкриває період · заголовки колонок сортують
              </div>
              {/* Гуртовий перерахунок. Він існував і раніше, але рендерився
                  ЛИШЕ в порожній таблиці угод («No trade data»), тобто на
                  готовому результаті його не було видно взагалі — і періоди
                  доводилось тиснути по одному (знайшов менеджер 17.08). */}
              {!embedded && !nested && onRecalc && (
                <button
                  onClick={async () => {
                    if (periodsRecalcProgress) return;
                    // 🚨 ПРОГРЕС ВИСТАВЛЯЄТЬСЯ ДО ПЕРШОГО await, А НЕ ПІСЛЯ.
                    // Головне вікно рахується хвилинами (вантажиться весь
                    // всесвіт), а кнопка при цьому лишалась із тим самим
                    // написом і активною: користувач бачив «нічого не
                    // відбувається» і тиснув ще раз, ставлячи в чергу ще одну
                    // задачу. Саме так 27.08 набралось десять однакових
                    // «Recalc Selected» в адмінці.
                    const total = extraPeriods.length + 1;   // +1 = головне вікно
                    setPeriodsRecalcProgress({ done: 0, total });
                    setRecalcLoading(true);
                    try {
                      await onRecalc();
                      setPeriodsRecalcProgress({ done: 1, total });
                      for (let i = 0; i < extraPeriods.length; i++) {
                        try { await handlePeriodRecalc(extraPeriods[i].idx); } catch (_) {}
                        setPeriodsRecalcProgress({ done: i + 2, total });
                      }
                    } finally {
                      setPeriodsRecalcProgress(null);
                      setRecalcLoading(false);
                    }
                  }}
                  disabled={!!periodsRecalcProgress}
                  style={{
                    marginLeft: 'auto', padding: '5px 12px', borderRadius: 6,
                    fontSize: 12, fontWeight: 600,
                    cursor: periodsRecalcProgress ? 'default' : 'pointer',
                    background: '#fff', color: C.primary,
                    border: `1px solid ${C.primary}`, opacity: periodsRecalcProgress ? 0.6 : 1,
                  }}
                  title="Порахувати повністю головний період і всі додаткові — по черзі, без кліку в кожен. Головне вікно займає кілька хвилин (вантажиться весь всесвіт), тому перший крок мовчазний — кнопка при цьому заблокована, повторний клік не потрібен."
                >
                  {periodsRecalcProgress
                    ? (periodsRecalcProgress.done === 0
                        ? '⏳ Рахую головне вікно…'
                        : `⏳ Рахую… ${periodsRecalcProgress.done}/${periodsRecalcProgress.total}`)
                    : `🔄 Перерахувати всі (${extraPeriods.length + 1})`}
                </button>
              )}
            </div>
            {/* Блоки дописаних вікон. Прикріплений блок входить у підсумок
                рядка (і в колонки зовнішньої таблиці), відкріплений — ні.
                Сенс саме в порівнянності: рядок із дописаними вікнами вже не
                стоїть на одній дошці з сусідами, у яких їх немає. */}
            {periodBlocks.length > 0 && (
              <div style={{ margin: '0 0 10px', padding: '8px 10px', background: '#F8FAFC',
                            border: `1px solid ${C.border}`, borderRadius: 6 }}>
                <div style={{ fontSize: 11, fontWeight: 700, color: C.textMd, marginBottom: 6 }}>
                  Дописані набори вікон
                </div>
                {periodBlocks.map(b => (
                  <label key={b.id} style={{
                    display: 'flex', alignItems: 'center', gap: 8, fontSize: 12,
                    color: C.text, padding: '3px 0',
                    cursor: blockBusy ? 'default' : 'pointer', opacity: blockBusy === b.id ? 0.5 : 1,
                  }}>
                    <input
                      type="checkbox" checked={b.included} disabled={!!blockBusy}
                      onChange={e => toggleBlock(b.id, e.target.checked)}
                    />
                    <span style={{ fontWeight: 600 }}>{b.label}</span>
                    <span style={{ color: C.textSm }}>· {b.n} вікон</span>
                    <span style={{ color: b.included ? C.success : C.textSm }}>
                      {b.included ? '· враховано в підсумку' : '· не враховано'}
                    </span>
                  </label>
                ))}
                <div style={{ fontSize: 11, color: C.textSm, marginTop: 5 }}>
                  Прикріплений набір змінює середні цього рядка — і його вже не порівняти
                  напряму зі стратегіями, у яких таких вікон немає.
                </div>
                {blockErr && (
                  <div style={{ fontSize: 11, color: C.danger, marginTop: 5 }}>⚠️ {blockErr}</div>
                )}
              </div>
            )}

            <PeriodsTable
              periods={periodsForTable}
              showAverages
              onExtend={extendMainPeriod}
              extendBusy={extendBusy}
              activeIdx={activeRowPos}
              rowStatus={(pos) => {
                const idx = periodsForTable[pos]?._idx;
                return loadingPeriods.has(idx) ? 'loading'
                     : periodErrors[idx]       ? 'error' : null;
              }}
              onRowClick={(p) => {
                if (p.is_main) { setActiveTab(0); return; }
                const pos = extraPeriods.findIndex(x => x.idx === p._idx);
                if (pos >= 0) setActiveTab(pos + 1);
              }}
            />
          </div>
        )}

        {activeTab > 0 ? (
          <PeriodSummaryView
            period={{ ...extraPeriods[activeTab - 1].p, ...(periodOverrides[extraPeriods[activeTab - 1].idx] || {}) }}
            periodIdx={extraPeriods[activeTab - 1].idx}
            parentData={data}
            fullDetail={periodFullDetails[extraPeriods[activeTab - 1].idx]}
            loading={loadingPeriods.has(extraPeriods[activeTab - 1].idx)}
            errorMsg={periodErrors[extraPeriods[activeTab - 1].idx]}
            onRecalc={() => handlePeriodRecalc(extraPeriods[activeTab - 1].idx)}
            onClearDetail={() => clearPeriodDetail(extraPeriods[activeTab - 1].idx)}
            onBack={() => setActiveTab(0)}
            onTickerClick={onTickerClick}
            bulkRecalcProgress={periodsRecalcProgress}
            queueIndex={activeTab - 1}
          />
        ) : (<>

        {/* ── Back + Title + strategy params — hidden in embedded (home/live view) ── */}
        {!embedded && (
        <div style={isMobile ? { ...S.backBar, flexDirection: 'column', alignItems: 'stretch' } : S.backBar}>
          {onBack && <button style={isMobile ? { ...S.backBtn, alignSelf: 'flex-start' } : S.backBtn} onClick={onBack}>← Back</button>}
          <div style={{ flex: 1 }}>
            <div style={S.title}>
              {data.id != null && <span style={{ color: C.textSm, fontWeight: 400, fontSize: 14, marginRight: 8 }}>(#{data.id})</span>}
              {data.name || 'Backtest Result'}
              {(() => {
                const eng = (_meta.engine || 'numba').toLowerCase();
                const isDefault = eng === 'numba';
                return (
                  <span style={{
                    display: 'inline-block', padding: '2px 8px', borderRadius: 4,
                    fontSize: 11, fontWeight: 600, marginLeft: 10, verticalAlign: 'middle',
                    background: isDefault ? '#E2E8F0' : eng === 'python' ? '#FEF3C7' : '#DBEAFE',
                    color:      isDefault ? '#64748B' : eng === 'python' ? '#92400E' : '#1E40AF',
                  }}>{eng}</span>
                );
              })()}
              {onToggleStar && (
                <span
                  onClick={onToggleStar}
                  title={data.is_saved ? 'Remove from favorites' : 'Add to favorites'}
                  style={{ marginLeft: 12, fontSize: 22, cursor: 'pointer', verticalAlign: 'middle',
                    color: data.is_saved ? '#F59E0B' : '#CBD5E1', userSelect: 'none' }}
                >
                  {data.is_saved ? '★' : '☆'}
                </span>
              )}
              {onCopyToForm && (
                <span
                  onClick={onCopyToForm}
                  title="Copy params to search form"
                  style={{ marginLeft: 8, fontSize: 18, cursor: 'pointer', verticalAlign: 'middle',
                    userSelect: 'none' }}
                >
                  📋
                </span>
              )}
              {data.is_saved && onPushToTrading && (
                <>
                  {pushTargets && pushTargets.length > 1 && (
                    <select
                      value={pushTarget || ''}
                      onChange={(e) => onPushTargetChange && onPushTargetChange(e.target.value)}
                      title="У який бот відправити стратегію"
                      style={{
                        marginLeft: 8, fontSize: 13, padding: '2px 5px', borderRadius: 4,
                        background: '#0f172a', color: '#fff', border: '1px solid #334155',
                        verticalAlign: 'middle', cursor: 'pointer',
                      }}
                    >
                      {pushTargets.map((t) => (
                        <option key={t.label} value={t.label}>{t.label}</option>
                      ))}
                    </select>
                  )}
                  <span
                    onClick={onPushToTrading}
                    title="Відправити стратегію в торгового робота"
                    style={{
                      marginLeft: 8, fontSize: 15, cursor: 'pointer', verticalAlign: 'middle',
                      padding: '2px 7px', borderRadius: 4, background: '#1E40AF',
                      color: '#fff', fontWeight: 600, userSelect: 'none',
                    }}
                  >
                    → Bot
                  </span>
                </>
              )}
            </div>
            <div style={{ marginTop: 6 }}>
              <StrategyParamsLabel params={{ ...params, ...(data._meta || {}) }} />
            </div>
          </div>
        </div>
        )}

        {/* ── Metric Rows ── Телефон у кабінеті: менші відступи, щоб кнопки
            графіка нижче вміщались у перший екран (менеджер 16.10). */}
        <div style={embedded && isMobile ? { ...S.card, marginBottom: 12 } : S.card}>
          <div style={{ ...S.metRowsGrid, gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr' }}>
            {(embedded ? [
              // Кабінет (менеджер 13.10): лише те, що зрозуміло вкладнику —
              // без Шарпа, просадки й відсотка прибуткових.
              // Суми капіталу тут дублювали картку зверху (менеджер 20.10).
              { label: 'Дохідність за весь період',      value: cabinetReturns.all?.value ?? '—',     color: cabinetReturns.all?.color },
              { label: 'Дохідність за останні 365 днів', value: cabinetReturns.last365?.value ?? '—', color: cabinetReturns.last365?.color },
              { label: 'Період роботи',          value: periodStrUk || '—' },
              { label: 'Кількість угод',         value: metrics.num_trades ?? '—' },
            ] : [
              { label: embedded ? 'Стартовий капітал' : 'Starting capital', value: fmtUsd(metrics.starting_capital) },
              { label: embedded ? 'Теперішній баланс' : 'Final balance',    value: fmtUsd(metrics.final_balance), color: metrics.final_balance >= metrics.starting_capital ? C.success : C.danger },
              { label: embedded ? 'Період' : 'Period',           value: periodStr ? periodStr.replace('\n', ' ') : '—' },
              { label: embedded ? 'Загальна дохідність' : 'Total return',     value: fmtPct(metrics.total_return), color: metrics.total_return >= 0 ? C.success : C.danger },
              { label: embedded ? 'Місячна дохідність' : 'Monthly return',   value: fmtAnnualPct(metrics.monthly_return, 2), color: metrics.monthly_return != null ? (metrics.monthly_return >= 0 ? C.success : C.danger) : C.text, hideOnShort: true },
              { label: embedded ? 'Річна дохідність' : 'Yearly return',    value: fmtAnnualPct(metrics.yearly_return, 1), color: metrics.yearly_return != null ? (metrics.yearly_return >= 0 ? C.success : C.danger) : C.text, hideOnShort: true },
              { label: embedded ? 'Кількість угод' : '# Trades',         value: metrics.num_trades ?? '—' },
              { label: embedded ? 'Відсоток прибуткових' : 'Win rate',         value: metrics.win_rate != null ? metrics.win_rate.toFixed(1) + '%' : '—', color: metrics.win_rate >= 60 ? C.success : metrics.win_rate >= 40 ? C.text : C.danger },
              { label: embedded ? 'Коеф. Шарпа' : 'Sharpe ratio',     value: fmtNum(metrics.sharpe_ratio), color: metrics.sharpe_ratio >= 1 ? C.success : metrics.sharpe_ratio >= 0 ? C.text : C.danger },
              { label: embedded ? 'Макс. просадка' : 'Max drawdown',     value: metrics.max_drawdown != null ? metrics.max_drawdown.toFixed(2) + '%' : '—', color: metrics.max_drawdown <= 10 ? C.success : metrics.max_drawdown <= 20 ? C.text : C.danger },
            ]).filter(m => !(m.hideOnShort && embedded && periodDays != null && periodDays < 7))
             .map((m, i) => (
              <div key={i} style={embedded && isMobile ? { ...S.metRow, padding: '5px 14px' } : S.metRow}>
                {/* Кабінет: однакова ширина підписів — значення стоять рівною
                    вертикаллю (менеджер 16.10). 230px = «Дохідність за останні 365 днів». */}
                <span style={embedded ? { ...S.metRowLabel, width: isMobile ? 'auto' : 230, minWidth: isMobile ? 0 : 230, flexShrink: 0 } : S.metRowLabel}>{m.label}</span>
                <span style={{ ...S.metRowValue, color: m.color || C.text }}>{m.value}</span>
              </div>
            ))}
          </div>
          {(() => {
            const buyTrades = trades.filter(t => t.action === 'BUY');
            const windowCnt   = buyTrades.filter(t => t.description === 'signal_window').length;
            const intradayCnt = buyTrades.filter(t => t.description === 'signal_intraday').length;
            if (windowCnt === 0 && intradayCnt === 0) return null;
            const total = windowCnt + intradayCnt;
            return (
              <div style={{ padding: '8px 20px 14px', display: 'flex', gap: 16 }}>
                <span style={{ fontSize: 12, color: C.textSm }}>Signal breakdown:</span>
                <span style={{ fontSize: 12, color: C.text }}>Window <b>{windowCnt}</b> <span style={{ color: C.textSm }}>({total ? Math.round(windowCnt/total*100) : 0}%)</span></span>
                <span style={{ fontSize: 12, color: C.text }}>Intraday <b>{intradayCnt}</b> <span style={{ color: C.textSm }}>({total ? Math.round(intradayCnt/total*100) : 0}%)</span></span>
              </div>
            );
          })()}
        </div>

        {/* ── Stability Metrics — hidden in embedded (VT/trading) context ── */}
        {!embedded && <div style={S.card}>
          <div style={S.chartHead}>
            <span style={S.chartTitle}>Стабільність стратегії</span>
          </div>
          <div style={{ padding: '16px 20px' }}>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 12 }}>
              {[
                {
                  key: 'profitable_months_pct',
                  label: 'Profit.Mo%',
                  tip: 'Частка прибуткових місяців за період бектесту. Що ближче до 100%, то рідше стратегія мала збитковий місяць.',
                  min: 70, ideal: 80,
                  fmt: v => v != null ? v.toFixed(1) + '%' : '—',
                  ok: v => v >= 80, warn: v => v >= 70,
                },
                {
                  key: 'worst_month',
                  label: 'Worst Mo%',
                  tip: 'Найгірший місяць за весь період (у %). Показує максимальний місячний "провал". Чим ближче до 0, тим м\'якіший найгірший сценарій.',
                  min: -5, ideal: -3,
                  fmt: v => v != null ? (v >= 0 ? '+' : '') + v.toFixed(1) + '%' : '—',
                  ok: v => v >= -3, warn: v => v >= -5,
                },
                {
                  key: 'calmar_ratio',
                  label: 'Calmar',
                  tip: 'Monthly return ÷ max drawdown. Скільки дохідності припадає на одиницю максимальної просадки. > 0.5 — добре.',
                  min: 0.2, ideal: 0.5,
                  fmt: v => fmtNum(v, 2),
                  ok: v => v >= 0.5, warn: v => v >= 0.2,
                },
                {
                  key: 'sharpe_ratio',
                  label: 'Sharpe',
                  tip: 'Середньомісячна дохідність, поділена на її волатильність (× √12 для річної). > 1.0 — стратегія дає стабільну дохідність відносно ризику.',
                  min: 1.0, ideal: 1.5,
                  fmt: v => fmtNum(v, 2),
                  ok: v => v >= 1.5, warn: v => v >= 1.0,
                },
              ].map(({ key, label, tip, min, ideal, fmt, ok, warn }) => {
                const v = metrics[key];
                const color = v == null ? C.textSm : ok(v) ? C.success : warn(v) ? C.text : C.danger;
                return (
                  <div key={key} style={{ background: C.pageBg, borderRadius: 8, padding: '12px 14px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 4, marginBottom: 6 }}>
                      <span style={{ fontSize: 12, fontWeight: 600, color: C.textMd }}>{label}</span>
                      <span title={tip} style={{ fontSize: 11, color: C.textSm, cursor: 'help', fontWeight: 700 }}>ⓘ</span>
                    </div>
                    <div style={{ fontSize: 20, fontWeight: 700, color }}>{fmt(v)}</div>
                    <div style={{ fontSize: 10, color: C.textSm, marginTop: 4 }}>
                      min ≥ {key === 'worst_month' ? min + '%' : min} · ideal ≥ {key === 'worst_month' ? ideal + '%' : ideal}
                    </div>
                  </div>
                );
              })}
            </div>
            <div style={{ marginTop: 12, fontSize: 12, color: C.textSm, fontStyle: 'italic' }}>
              Стратегія, що задовольняє всі 4 умови, з великою імовірністю буде прибутковою в будь-який довільний місяць.
            </div>
          </div>
        </div>}

        {/* ── Equity Curve ── */}
        <div style={S.card}>
          <div style={isMobile ? { ...S.chartHead, padding: '10px 12px', gap: 8 } : S.chartHead}>
            <span style={S.chartTitle}>Equity Curve</span>
            {/* Телефон: 5 фільтрів + Value/Performance у рядок не вміщуються —
                кнопка Performance вилазила за екран (менеджер 13.10). */}
            {/* Телефон: вужчі кнопки — 5 фільтрів + Value/Performance одним
                рядком (менеджер 16.10); перенос лишається запасом. */}
            <div style={{ display: 'flex', gap: isMobile ? 6 : 16, alignItems: 'center', flexWrap: 'wrap', minWidth: 0, maxWidth: '100%' }}>
              {/* Range filter */}
              <div style={{ ...S.filterRow, gap: isMobile ? 4 : 6, flexWrap: 'wrap' }}>
                {RANGE_FILTERS.map(f => (
                  <button key={f} style={isMobile ? { ...S.filterBtn(rangeFilter === f), padding: '4px 6px' } : S.filterBtn(rangeFilter === f)}
                    onClick={() => setRangeFilter(f)}>{f}</button>
                ))}
              </div>
              {/* Value / Performance toggle */}
              <div style={{ display: 'flex', gap: 4 }}>
                <button style={isMobile ? { ...S.modeBtn(chartMode === 'value'), padding: '4px 6px' } : S.modeBtn(chartMode === 'value')} onClick={() => setChartMode('value')}>Value</button>
                <button style={isMobile ? { ...S.modeBtn(chartMode === 'performance'), padding: '4px 6px' } : S.modeBtn(chartMode === 'performance')} onClick={() => setChartMode('performance')}>Performance</button>
              </div>
              {/* Trend + Market Volatility overlays: analyzer-only, hidden on
                  the embedded user bot view */}
              {!embedded && (
                <>
                  {/* Trend line toggle */}
                  <button
                    style={{
                      padding: '4px 10px', borderRadius: 5, fontSize: 12, cursor: 'pointer',
                      background: showTrend ? '#F97316' : 'transparent',
                      color: showTrend ? '#fff' : C.textMd,
                      border: `1px solid ${showTrend ? '#F97316' : C.border}`,
                    }}
                    onClick={() => setShowTrend(v => !v)}
                    title="Show/hide trend line"
                  >
                    Тренд
                  </button>
                  {/* Volatility overlay toggle */}
                  <button
                    style={{
                      padding: '4px 10px', borderRadius: 5, fontSize: 12, cursor: 'pointer',
                      background: showVolatility ? '#F59E0B' : 'transparent',
                      color: showVolatility ? '#fff' : C.textMd,
                      border: `1px solid ${showVolatility ? '#F59E0B' : C.border}`,
                    }}
                    onClick={() => setShowVolatility(v => !v)}
                    title="Overlay market volatility index"
                  >
                    {volLoading ? '⏳' : '📊'} Market Volatility
                  </button>
                </>
              )}
            </div>
          </div>
          <div style={{ padding: '16px 20px' }}>
            {equityAnomalies.length > 0 && (
              <div style={{
                background: '#fef3c7',
                border: '1px solid #f59e0b',
                borderRadius: 6,
                padding: '6px 12px',
                marginBottom: 8,
                fontSize: 12,
                color: '#92400e',
              }}>
                ⚠️ Нечислові значення в кривій на датах:{' '}
                {equityAnomalies.slice(0, 3).map(p => p.date).join(', ')}
                {equityAnomalies.length > 3 && ` +${equityAnomalies.length - 3} більше`}
                {' '}— ці точки неможливо намалювати, тому їх пропущено. Малий
                баланс аномалією НЕ вважається: падіння рахунку показується як є.
              </div>
            )}
            {mergedChartData.length > 0 ? (
              <ResponsiveContainer width="100%" height={volOn ? 320 : 280}>
                <LineChart
                  data={mergedChartData}
                  margin={{ top: 4, right: volOn ? 56 : 8, left: 4, bottom: 4 }}
                  onClick={handleChartClick}
                  style={{ cursor: 'pointer' }}
                >
                  <CartesianGrid strokeDasharray="3 3" stroke={C.border} horizontal={false} />
                  <XAxis
                    dataKey="date"
                    tick={{ fontSize: 11, fill: C.textSm }}
                    tickLine={false}
                    tickFormatter={v => v.slice(5)}
                    interval="preserveStartEnd"
                  />
                  <YAxis
                    yAxisId="left"
                    domain={chartMode === 'value'
                      ? [0, yMax + yPad]
                      : [yMin - yPad, yMax + yPad]}
                    ticks={yTicksValue}
                    tick={{ fontSize: 11, fill: C.textSm }}
                    tickLine={false}
                    tickFormatter={tickFmt}
                    width={72}
                  />
                  {volOn && (
                    <YAxis
                      yAxisId="right"
                      orientation="right"
                      tick={{ fontSize: 10, fill: '#F59E0B' }}
                      tickLine={false}
                      tickFormatter={v => v?.toFixed(0)}
                      width={44}
                    />
                  )}
                  <Tooltip content={<CustomTooltip mode={chartMode} />} />
                  {/* Horizontal grid lines at custom Y ticks */}
                  {yTicksValue && yTicksValue.map(tick => (
                    <ReferenceLine key={tick} yAxisId="left" y={tick} stroke={C.border} strokeDasharray="3 3" strokeWidth={1} />
                  ))}
                  {chartMode === 'performance' && (
                    <ReferenceLine yAxisId="left" y={0} stroke={C.border} strokeDasharray="4 4" />
                  )}
                  {chartMode === 'value' && metrics.starting_capital && (
                    <ReferenceLine yAxisId="left" y={metrics.starting_capital} stroke="#94A3B8"
                      strokeDasharray="4 4" label={{ value: 'Start', position: 'right', fontSize: 10, fill: C.textSm }} />
                  )}
                  <Line
                    yAxisId="left"
                    type="monotone"
                    dataKey={chartField}
                    stroke={C.primary}
                    strokeWidth={2}
                    dot={false}
                    activeDot={{ r: 4, fill: C.primary }}
                  />
                  {/* Лінія тренду (апроксимована пряма) */}
                  {trendOn && (
                    <Line
                      yAxisId="left"
                      type="monotone"
                      dataKey={chartMode === 'value' ? 'trend' : 'trendPerf'}
                      stroke="#F97316"
                      strokeWidth={1.5}
                      strokeDasharray="6 3"
                      dot={false}
                      activeDot={false}
                      name="Trend"
                    />
                  )}
                  {volOn && (
                    <Line
                      yAxisId="right"
                      type="monotone"
                      dataKey="vol_index"
                      stroke="#F59E0B"
                      strokeWidth={1.5}
                      dot={false}
                      strokeDasharray="4 2"
                      connectNulls
                      name="Volatility Index"
                    />
                  )}
                </LineChart>
              </ResponsiveContainer>
            ) : (
              <div style={{ height: 280, display: 'flex', alignItems: 'center', justifyContent: 'center', color: C.textSm }}>
                {embedded ? 'Дані графіка з’являться після початку торгів' : 'No equity data available'}
              </div>
            )}

            {/* Volatility legend + correlation */}
            {volOn && (
              <div style={{ marginTop: 10, display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: C.textMd }}>
                  <svg width="20" height="4"><line x1="0" y1="2" x2="20" y2="2" stroke="#F59E0B" strokeWidth="1.5" strokeDasharray="4 2"/></svg>
                  Market Volatility Index (right axis)
                </div>
                {volLoading && (
                  <span style={{ fontSize: 12, color: C.textSm }}>Loading volatility data…</span>
                )}
                {!volLoading && volCorrelation !== null && (
                  <div style={{
                    padding: '6px 12px',
                    background: '#FEFCE8', border: '1px solid #FDE68A', borderRadius: 7,
                    fontSize: 12, display: 'flex', alignItems: 'center', gap: 6,
                  }}>
                    <span style={{ color: C.textMd }}>Correlation with volatility:</span>
                    <span style={{
                      fontWeight: 700,
                      color: Math.abs(volCorrelation) < 0.3 ? C.textMd
                           : volCorrelation > 0 ? '#16A34A' : '#DC2626',
                    }}>
                      {volCorrelation > 0 ? '+' : ''}{volCorrelation.toFixed(2)}
                    </span>
                    <span style={{ color: C.textMd }}>—</span>
                    <span style={{ color: C.textMd }}>
                      {volCorrelation > 0.7  ? '✅ Loves volatility'
                     : volCorrelation < -0.7 ? '🛡️ Prefers calm market'
                     : Math.abs(volCorrelation) < 0.3 ? '⚖️ Market-neutral'
                     : volCorrelation > 0    ? 'Slightly +correlated'
                     :                         'Slightly −correlated'}
                    </span>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        {/* ── Ticker Statistics ── */}
        {hasTicker && tickerStats.length > 0 && (
          <div style={S.card}>
            <div
              style={{ ...S.tableHead, cursor: 'pointer', userSelect: 'none' }}
              onClick={() => setTickerStatsCollapsed(v => !v)}
            >
              <span style={S.tableTitle}>
                Ticker Statistics — {tickerStats.length} tickers, {trades.length} trades
              </span>
              <span style={{ fontSize: 12, color: C.textMd }}>
                {tickerStatsCollapsed ? '▶ Show' : '▼ Hide'}
              </span>
            </div>
            {!tickerStatsCollapsed && lifeSummary && lifeSummary.tickers > 0 && (
              <div style={{
                padding: '8px 12px', fontSize: 12, color: C.textMd,
                borderBottom: `1px solid ${C.border}`, background: '#F8FAFC',
              }}>
                <b>Склад реєстру в цьому прогоні:</b>{' '}
                торгувалось {lifeSummary.tickers} компаній ·{' '}
                <span style={{ color: '#15803D' }}>зʼявились у межах наших даних: {lifeSummary.listed_within_data}</span> ·{' '}
                <span style={{ color: '#B91C1C' }}>зникли з біржі: {lifeSummary.delisted}</span> ·{' '}
                дані обриваються: {lifeSummary.data_ends_early} ·{' '}
                торгувались і раніше: {lifeSummary.store_floor}
                {lifeSummary.uncertain ? ` · невизначено: ${lifeSummary.uncertain}` : ''}
                {lifeSummary.no_bars ? ` · без барів: ${lifeSummary.no_bars}` : ''}
                <div style={{ marginTop: 4, color: C.textSm }}>
                  ⚠️ «Data from» — не дата виходу на біржу. Для більшості великих компаній
                  вона впирається в межу нашого сховища: перший бар AAPL — 2020-01-02,
                  хоча Apple торгується з 1980 року.
                </div>
              </div>
            )}
            {!tickerStatsCollapsed && (() => {
              const TSortHdr = ({ col, label, style = {} }) => {
                const active = tickerSortCol === col;
                const icon = !active ? '↕' : tickerSortDir === 'asc' ? '↑' : '↓';
                return (
                  <th onClick={() => handleTickerSort(col)} style={{
                    cursor: 'pointer', userSelect: 'none', whiteSpace: 'nowrap',
                    padding: '8px 12px',
                    background: active ? `${C.primary}10` : '#F8FAFC',
                    color: active ? C.primary : C.textMd,
                    fontWeight: 600, fontSize: 12,
                    border: `1px solid ${C.border}`,
                    borderBottom: `2px solid ${active ? C.primary : C.border}`,
                    textAlign: 'left', ...style,
                  }}>
                    {label} <span style={{ opacity: active ? 1 : 0.3, fontSize: 11 }}>{icon}</span>
                  </th>
                );
              };
              return (
                <div style={{ overflowX: 'auto' }}>
                  <table style={S.table}>
                    <thead>
                      <tr>
                        <TSortHdr col="ticker"     label="Ticker" />
                        <TSortHdr col="total"      label="Total trades" />
                        <TSortHdr col="buys"       label="BUYs" />
                        <TSortHdr col="sells"      label="SELLs" />
                        <TSortHdr col="winRate"    label="Win rate" />
                        <TSortHdr col="avgPnl"     label="Avg P&L%" />
                        {showVolatilityCol && <TSortHdr col="volatility" label="Volatility (price range %)" />}
                        {hasTickerLife && (<>
                          <TSortHdr col="firstBar"   label="Data from" />
                          <TSortHdr col="lastBar"    label="Data to" />
                          <TSortHdr col="origin"     label="Ticker life" />
                        </>)}
                      </tr>
                    </thead>
                    <tbody>
                      {sortedTickerStats.map((s, i) => (
                        <tr
                          key={i}
                          style={{ background: tickerFilter === s.ticker ? `${C.primary}10` : i % 2 === 0 ? '#fff' : '#F8FAFC', cursor: 'pointer' }}
                          onClick={() => {
                            setTickerFilter(tickerFilter === s.ticker ? null : s.ticker);
                            setTradePage(0);
                            if (tradesCardRef.current) tradesCardRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
                          }}
                          title={`Показати угоди по ${s.ticker}`}
                        >
                          <td style={{ ...S.td, fontWeight: 600, color: C.primary }}>
                            {onTickerClick ? (
                              <button
                                onClick={(e) => { e.stopPropagation(); navigateByTicker(s.ticker); }}
                                style={{ background: 'none', border: 'none', padding: 0, fontWeight: 600, fontSize: 12, color: C.primary, cursor: 'pointer', textDecoration: 'underline' }}
                              >
                                {s.ticker}
                              </button>
                            ) : s.ticker}
                          </td>
                          <td style={{ ...S.td, fontWeight: 600 }}>{s.total}</td>
                          <td style={S.td}>{s.buys}</td>
                          <td style={S.td}>{s.sells}</td>
                          <td style={S.td}>
                            {s.winRate != null
                              ? <span style={{ fontWeight: 600, color: s.winRate >= 60 ? C.success : s.winRate >= 40 ? C.text : C.danger }}>
                                  {s.winRate.toFixed(1)}%
                                </span>
                              : <span style={{ color: C.textSm }}>—</span>}
                          </td>
                          <td style={S.td}>
                            {s.avgPnl != null
                              ? <span style={{ fontWeight: 600, color: s.avgPnl >= 0 ? C.success : C.danger }}>
                                  {fmtPct(s.avgPnl)}
                                </span>
                              : <span style={{ color: C.textSm }}>—</span>}
                          </td>
                          {showVolatilityCol && <td style={S.td}>
                            {s.volatility != null
                              ? <span style={{ color: s.volatility >= 20 ? C.danger : s.volatility >= 10 ? '#D97706' : C.text }}>
                                  {s.volatility.toFixed(2)}%
                                </span>
                              : <span style={{ color: C.textSm }}>—</span>}
                          </td>}
                          {hasTickerLife && (<>
                            <td style={{ ...S.td, whiteSpace: 'nowrap' }}>
                              {s.firstBar || <span style={{ color: C.textSm }}>—</span>}
                            </td>
                            <td style={{ ...S.td, whiteSpace: 'nowrap' }}>
                              {s.lastBar || <span style={{ color: C.textSm }}>—</span>}
                            </td>
                            <td style={S.td}>{lifeBadge(s)}</td>
                          </>)}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              );
            })()}
          </div>
        )}

        {/* ── Trades Table ── */}
        <div style={S.card} ref={tradesCardRef}>
          <div style={S.tableHead}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
              <span style={S.tableTitle}>
                Trades ({filteredTrades.length}
                {tradeFilter !== 'ALL' ? ` ${tradeFilter}` : ''})
              </span>
              {tickerFilter && (
                <span style={{
                  display: 'inline-flex', alignItems: 'center', gap: 4,
                  padding: '2px 8px', borderRadius: 99, fontSize: 12, fontWeight: 600,
                  background: `${C.primary}15`, color: C.primary, border: `1px solid ${C.primary}40`,
                }}>
                  {tickerFilter}
                  <button
                    onClick={() => { setTickerFilter(null); setTradePage(0); }}
                    style={{ background: 'none', border: 'none', cursor: 'pointer', color: C.primary, padding: 0, fontSize: 13, lineHeight: 1, fontWeight: 700 }}
                    title="Скинути фільтр"
                  >✕</button>
                </span>
              )}
            </div>
            <div style={S.tabRow}>
              {['ALL', 'BUY', 'SELL'].map(f => (
                <button key={f} style={S.tabBtn(tradeFilter === f)}
                  onClick={() => { setTradeFilter(f); setTradePage(0); }}>{f}</button>
              ))}
            </div>
          </div>
          {(() => {
            const SortHeader = ({ col, label, style = {} }) => {
              const isActive = sortCol === col;
              const icon = !isActive ? '↕' : sortDir === 'asc' ? '↑' : '↓';
              return (
                <th
                  onClick={() => handleSort(col)}
                  style={{
                    cursor: 'pointer', userSelect: 'none', whiteSpace: 'nowrap',
                    padding: '8px 12px',
                    background: isActive ? `${C.primary}10` : '#F8FAFC',
                    color: isActive ? C.primary : C.textMd,
                    fontWeight: 600, fontSize: 12,
                    border: `1px solid ${C.border}`,
                    borderBottom: `2px solid ${isActive ? C.primary : C.border}`,
                    textAlign: 'left',
                    ...style,
                  }}
                >
                  {label}{' '}
                  <span style={{ opacity: isActive ? 1 : 0.3, fontSize: 11 }}>{icon}</span>
                </th>
              );
            };
            return (
          <div style={{ overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}>
          <table style={S.table}>
            <thead>
              <tr>
                <SortHeader col="id"         label="#"           />
                <SortHeader col="time"       label="Time (ET)"   />
                {hasTicker  && <SortHeader col="ticker"  label="Ticker"      />}
                <SortHeader col="action"     label="Action"      />
                <SortHeader col="qty"        label="Qty"         />
                <SortHeader col="price"      label="Price"       />
                <SortHeader col="start_sum"  label="Starting sum ($)" />
                <SortHeader col="duration"   label="Duration"    style={{ width: 90 }} />
                <SortHeader col="final_sum"  label="Final sum ($)" />
                <SortHeader col="change_usd" label="Changes ($)" style={{ width: 100 }} />
                <SortHeader col="change_pct" label="Changes (%)" style={{ width: 90 }} />
                {hasDetails && <SortHeader col="details" label="Details"     style={{ minWidth: 260 }} />}
              </tr>
            </thead>
            <tbody>
              {displayedTrades.length === 0 && (
                <tr>
                  <td colSpan={10 + (hasTicker ? 1 : 0) + (hasDetails ? 1 : 0)} style={{ ...S.td, textAlign: 'center', padding: 28, color: C.textSm }}>
                    {trades.length === 0 ? (
                      embedded ? 'Угод поки немає' : recalcLoading ? (
                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                          <span style={{
                            display: 'inline-block', width: 16, height: 16,
                            border: `2px solid ${C.border}`, borderTopColor: C.primary,
                            borderRadius: '50%', animation: 'spin 0.8s linear infinite',
                          }} />
                          Recalculating…
                        </span>
                      ) : (
                        <span>
                          No trade data.{' '}
                          {onRecalc ? (
                            <>
                              <button
                                onClick={async () => {
                                  setRecalcLoading(true);
                                  try {
                                    await onRecalc();
                                    if (extraPeriods.length > 0) {
                                      setPeriodsRecalcProgress({ done: 0, total: extraPeriods.length });
                                      for (let i = 0; i < extraPeriods.length; i++) {
                                        try { await handlePeriodRecalc(extraPeriods[i].idx); } catch (_) {}
                                        setPeriodsRecalcProgress({ done: i + 1, total: extraPeriods.length });
                                      }
                                      setPeriodsRecalcProgress(null);
                                    }
                                  } finally {
                                    setRecalcLoading(false);
                                  }
                                }}
                                style={{ background: 'none', border: 'none', color: C.primary, cursor: 'pointer', textDecoration: 'underline', fontSize: 13, padding: 0 }}
                              >
                                🔄 Recalculate{extraPeriods.length > 0 ? ` + ${extraPeriods.length} periods` : ''}
                              </button>
                              {periodsRecalcProgress && (
                                <span style={{ marginLeft: 10, fontSize: 12, color: C.textMd, display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                                  <span style={{
                                    display: 'inline-block', width: 12, height: 12,
                                    border: `2px solid ${C.border}`, borderTopColor: C.primary,
                                    borderRadius: '50%', animation: 'spin 0.8s linear infinite',
                                  }} />
                                  Periods: {periodsRecalcProgress.done}/{periodsRecalcProgress.total}
                                </span>
                              )}
                            </>
                          ) : 'Use 🔄 Recalculate on this result to load trades.'}
                        </span>
                      )
                    ) : 'No trades match the current filter.'}
                  </td>
                </tr>
              )}
              {displayedTrades.map((t, i) => {
                const isBuy  = t.action === 'BUY';
                const chgOk  = t.change_usd != null && t.change_usd > 0;
                const chgBad = t.change_usd != null && t.change_usd < 0;
                const pnlOk  = t.pnl_pct != null && t.pnl_pct > 0;
                const pnlBad = t.pnl_pct != null && t.pnl_pct < 0;
                const detailColor = isBuy ? C.textSm : pnlOk ? C.success : pnlBad ? C.danger : C.textMd;
                const globalIdx = pageSize === 'all' ? i : tradePage * (typeof pageSize === 'number' ? pageSize : 50) + i;
                const isHighlighted = highlightedTradeIdx !== null && globalIdx === highlightedTradeIdx;
                return (
                  <tr
                    key={i}
                    ref={isHighlighted ? highlightedRowRef : null}
                    style={{
                      background: isHighlighted
                        ? '#FEF9C3'
                        : isBuy ? C.buyBg : C.sellBg,
                      outline: isHighlighted ? '2px solid #EAB308' : 'none',
                      transition: 'background 0.3s',
                    }}
                  >
                    <td style={S.td}>{pageSize === 'all' ? i + 1 : tradePage * pageSize + i + 1}</td>
                    <td style={S.td}>{fmtTradeTime(t.time)}</td>
                    {hasTicker && (
                      <td style={{ ...S.td, fontWeight: 600 }}>
                        {onTickerClick ? (
                          <button
                            onClick={() => handleTickerClick(t)}
                            style={{ background: 'none', border: 'none', padding: 0, fontWeight: 600, fontSize: 12, color: C.primary, cursor: 'pointer', textDecoration: 'underline' }}
                          >
                            {t.ticker}
                          </button>
                        ) : t.ticker}
                      </td>
                    )}
                    <td style={S.td}>
                      <span style={{
                        display: 'inline-block', padding: '2px 8px', borderRadius: 4,
                        fontSize: 11, fontWeight: 700,
                        background: isBuy ? '#DBEAFE' : '#DCFCE7',
                        color: isBuy ? C.primary : C.success,
                      }}>{t.action}</span>
                    </td>
                    <td style={S.td}>{t.qty != null ? Number(t.qty).toFixed(4) : '—'}</td>
                    <td style={S.td}>{t.price != null ? fmtUsd(t.price) : '—'}</td>
                    <td style={S.td}>{startSum(t) != null ? fmtUsd(startSum(t)) : '—'}</td>
                    <td style={{ ...S.td, color: C.textMd }}>
                      {isBuy
                        ? <span style={{ color: C.textSm }}>open</span>
                        : fmtDuration(t.time, t.buy_time)}
                    </td>
                    <td style={S.td}>
                      {isBuy
                        ? <span style={{ color: C.textSm }}>—</span>
                        : (finalSum(t) != null ? fmtUsd(finalSum(t)) : '—')}
                    </td>
                    <td style={S.td}>
                      {t.change_usd != null
                        ? <span style={{ color: chgOk ? C.success : chgBad ? C.danger : C.textMd, fontWeight: 600 }}>
                            {t.change_usd >= 0 ? '+' : ''}{fmtUsd(t.change_usd)}
                          </span>
                        : <span style={{ color: C.textSm }}>—</span>}
                    </td>
                    <td style={S.td}>
                      {t.pnl_pct != null
                        ? <span style={{ color: pnlOk ? C.success : pnlBad ? C.danger : C.textMd, fontWeight: 600 }}>
                            {fmtPct(t.pnl_pct)}
                          </span>
                        : <span style={{ color: C.textSm }}>—</span>}
                    </td>
                    {hasDetails && (
                      <td style={{ ...S.td, fontSize: 11, color: detailColor, whiteSpace: 'normal', minWidth: 260 }}>
                        {getDetails(t, detailParams)}
                      </td>
                    )}
                  </tr>
                );
              })}
            </tbody>
          </table>
          </div>
          );
          })()}
          {/* Pagination */}
          <div style={{
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            padding: '10px 16px', borderTop: `1px solid ${C.border}`, flexWrap: 'wrap', gap: 8,
          }}>
            {/* Left: count */}
            <div style={{ fontSize: 13, color: C.textSm }}>
              {sortedTrades.length === 0
                ? '0 trades'
                : pageSize === 'all'
                  ? `All ${sortedTrades.length} trades`
                  : `${tradePage * pageSize + 1}–${Math.min((tradePage + 1) * pageSize, sortedTrades.length)} of ${sortedTrades.length}`
              }
              {sortedTrades.length !== trades.length && (
                <span style={{ marginLeft: 6, color: C.primary }}>(filtered from {trades.length})</span>
              )}
            </div>

            {/* Center: navigation */}
            {pageSize !== 'all' && totalTradePages > 1 && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                <button onClick={() => setTradePage(0)} disabled={tradePage === 0} style={paginBtn(tradePage === 0)}>«</button>
                <button onClick={() => setTradePage(p => Math.max(0, p - 1))} disabled={tradePage === 0} style={paginBtn(tradePage === 0)}>‹ Prev</button>
                {Array.from({ length: Math.min(5, totalTradePages) }, (_, i) => {
                  let start = Math.max(0, tradePage - 2);
                  let end   = Math.min(totalTradePages - 1, start + 4);
                  start = Math.max(0, end - 4);
                  return start + i;
                }).filter(p => p < totalTradePages).map(p => (
                  <button key={p} onClick={() => setTradePage(p)} style={{
                    ...paginBtn(false),
                    background: p === tradePage ? C.primary : 'transparent',
                    color:      p === tradePage ? '#fff'     : C.text,
                    fontWeight: p === tradePage ? 700        : 400,
                    minWidth:   32,
                  }}>{p + 1}</button>
                ))}
                <button onClick={() => setTradePage(p => Math.min(totalTradePages - 1, p + 1))} disabled={tradePage >= totalTradePages - 1} style={paginBtn(tradePage >= totalTradePages - 1)}>Next ›</button>
                <button onClick={() => setTradePage(totalTradePages - 1)} disabled={tradePage >= totalTradePages - 1} style={paginBtn(tradePage >= totalTradePages - 1)}>»</button>
              </div>
            )}

            {/* Right: rows per page */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <span style={{ fontSize: 12, color: C.textSm }}>Rows:</span>
              <div style={{ display: 'flex', gap: 4 }}>
                {PAGE_SIZE_OPTIONS.map(opt => (
                  <button key={opt} onClick={() => handlePageSize(opt)} style={{
                    padding: '3px 10px', borderRadius: 5, fontSize: 12, cursor: 'pointer',
                    border:      `1px solid ${pageSize === (opt === 'all' ? 'all' : +opt) ? C.primary : C.border}`,
                    background:  pageSize === (opt === 'all' ? 'all' : +opt) ? C.primary : 'transparent',
                    color:       pageSize === (opt === 'all' ? 'all' : +opt) ? '#fff' : C.text,
                    fontWeight:  pageSize === (opt === 'all' ? 'all' : +opt) ? 700 : 400,
                  }}>
                    {opt === 'all' ? 'All' : opt}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>
        </>)}

      {/* 🚨 `!nested` тут не косметика. Ендпоінт стійкості працює з РЯДКОМ
          результату (/api/analyzer/results/{id}/stability), а не з періодом —
          тож кнопка всередині періоду рахувала б стійкість усього прогону й
          підписувала її як стійкість періоду. Плюс візуально це давало дві
          однакові кнопки на сторінці (знайшов юзер 13.08). */}
      {!embedded && !nested && (
        <div style={{ ...S.card, padding: '18px 22px', marginTop: 4 }}>
          <div style={{ fontSize: 15, fontWeight: 700, color: C.textLg, marginBottom: 6 }}>
            Перевірка стійкості
          </div>
          <div style={{ fontSize: 12, color: C.textSm, marginBottom: 12, maxWidth: 820 }}>
            Ганяє цю саму стратегію на <b>відрізках часу</b> всередині основного періоду
            рядка (випадкових або на всіх точках входу) й показує <b>найгірший</b> з отриманих результатів. Головне вікно
            рядка для цього не годиться: стратегію на ньому й шукали, тобто воно
            зміщене за побудовою. Мала дохідність тут — не поганий результат; погано,
            коли найгірше вікно збиткове.
            {' '}Кожна перевірка <b>додається</b> до попередніх, а середні під таблицею
            рахуються по всіх періодах разом.
            {' '}Належність компанії до всесвіту рахується <b>по кожному бару</b>: інакше
            вікно 2024 року торгувало б лише тими, хто існував у 2020.
          </div>

          {!stabLoading && (
            <div style={{ border: `1px solid ${C.border}`, borderRadius: 8, padding: '10px 12px',
                          marginBottom: 12, background: '#F8FAFC' }}>
              {/* Той самий вибір, що в генераторі форми пошуку. Тут він
                  потрібен заради ДОВЖИНИ: стратегію, знайдену на річних
                  вікнах, можна перебрати по всіх точках входу дворічними. */}
              <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginBottom: 8 }}>
                {[['random', 'випадкові вікна'], ['sweep', 'усі точки входу']].map(([v, label]) => (
                  <label key={v} style={{ display: 'flex', alignItems: 'center', gap: 5,
                                          fontSize: 12, color: C.textMd, cursor: 'pointer' }}>
                    <input type="radio" name="stabmode" checked={(stabCfg.mode || 'random') === v}
                      onChange={() => setStabCfg({ ...stabCfg, mode: v })} />
                    {label}
                  </label>
                ))}
              </div>
              <div style={{ display: 'flex', alignItems: 'flex-end', gap: 12, flexWrap: 'wrap' }}>
                <label style={{ fontSize: 12, color: C.textMd }}>
                  Довжина періоду, місяців<br />
                  <input
                    type="number" min={12} value={stabCfg.months}
                    onChange={e => setStabCfg({ ...stabCfg, months: e.target.value })}
                    title="Менше 12 задати не можна: коротше вікно міряє епізод, а не стратегію"
                    style={{ marginTop: 4, width: 110, height: 32, borderRadius: 6,
                             border: `1px solid ${C.border}`, padding: '0 8px', fontSize: 12 }}
                  />
                </label>
                {!stabSweep && (
                  <label style={{ fontSize: 12, color: C.textMd }}>
                    Кількість періодів<br />
                    <input
                      type="number" min={1} value={stabCfg.count}
                      onChange={e => setStabCfg({ ...stabCfg, count: e.target.value })}
                      style={{ marginTop: 4, width: 110, height: 32, borderRadius: 6,
                               border: `1px solid ${C.border}`, padding: '0 8px', fontSize: 12 }}
                    />
                  </label>
                )}
                <label style={{ fontSize: 12, color: C.textMd, display: 'flex',
                                alignItems: 'center', gap: 6, paddingBottom: 8 }}>
                  <input
                    type="checkbox" checked={!!stabCfg.extraEnabled}
                    onChange={e => setStabCfg({ ...stabCfg, extraEnabled: e.target.checked })}
                  />
                  Додати випадкові дні до кожного періоду
                </label>
                {stabCfg.extraEnabled && ['extraFrom', 'extraTo'].map(k => (
                  <label key={k} style={{ fontSize: 12, color: C.textMd }}>
                    {k === 'extraFrom' ? 'від, днів' : 'до, днів'}<br />
                    <input
                      type="number" min={0} value={stabCfg[k]}
                      onChange={e => setStabCfg({ ...stabCfg, [k]: e.target.value })}
                      style={{ marginTop: 4, width: 90, height: 32, borderRadius: 6,
                               border: `1px solid ${C.border}`, padding: '0 8px', fontSize: 12 }}
                    />
                  </label>
                ))}
                <label style={{ fontSize: 12, color: C.textMd, display: 'flex',
                                alignItems: 'center', gap: 6, paddingBottom: 8 }}>
                  <input
                    type="checkbox" checked={!!stabCfg.capitalLadder}
                    onChange={e => setStabCfg({ ...stabCfg, capitalLadder: e.target.checked })}
                  />
                  Ще й драбина капіталу (50/100/200 тис.)
                </label>
              </div>
              <div style={{ fontSize: 11, color: C.textSm, marginTop: 8, maxWidth: 820 }}>
                {stabSweep ? (
                  <>По одному вікну з <b>кожного торгового дня</b> основного періоду рядка,
                  де вікно ще вміщається до його кінця: без повторів і без пропусків. Що
                  довше вікно, то менше точок входу (на 5 роках: річні ≈ 1000, дворічні ≈
                  750). Рахується довше за випадкові — десятки хвилин.</>
                ) : (
                  <>Вікна беруться випадково з основного періоду рядка (обрізаного останнім
                  днем, за який є дані).</>
                )}
                {' '}Вікно, що не вміщається цілком, не створюється взагалі — обрізане
                виглядало б у таблиці як повноцінне і псувало б середні.
                {stabCfg.capitalLadder && (
                  <> Драбина капіталу коштує три прогони й відповідає на одне питання:
                  чи не гойдає результат сама лише зміна суми. Якщо гойдає — різниця між
                  цією стратегією й сусідньою в таблиці нічого не означає.</>
                )}
              </div>
            </div>
          )}

          {!stabLoading && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap',
                          marginBottom: stabRuns.length ? 14 : 0 }}>
              <button
                onClick={runStabilityCheck}
                style={{
                  padding: '10px 22px', height: 40, borderRadius: 8, fontSize: 13,
                  fontWeight: 600, cursor: 'pointer',
                  background: stabRuns.length ? '#fff' : C.primary,
                  color: stabRuns.length ? C.primary : '#fff',
                  border: stabRuns.length ? `1px solid ${C.primary}` : 'none',
                }}
              >
                {stabRuns.length ? 'Перевірити стійкість ще раз' : 'Перевірити стійкість стратегії'}
              </button>
              {stabRuns.length > 0 && (
                <span style={{ fontSize: 11, color: C.textSm, maxWidth: 520 }}>
                  Перевірок збережено: <b>{stabRuns.length}</b>. Кожна наступна бере нові
                  випадкові вікна й додається до попередніх.
                </span>
              )}
            </div>
          )}

          {stabLoading && (
            <div style={{ marginBottom: 14 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <span style={{
                  display: 'inline-block', width: 16, height: 16, flexShrink: 0,
                  border: `2px solid ${C.border}`, borderTopColor: '#F59E0B',
                  borderRadius: '50%', animation: 'spin 0.8s linear infinite',
                }} />
                <span style={{ fontSize: 13, color: C.textMd }}>
                  {stabProgress?.phase === 'queued'
                    ? 'У черзі на сервері — почнеться, щойно звільниться слот…'
                    : stabProgress && stabProgress.total && stabProgress.done
                      ? `Пораховано ${stabProgress.done} з ${stabProgress.total}…`
                      : 'Готую дані на всю історію (перший крок найдовший)…'}
                </span>
              </div>
              <div style={{ fontSize: 11, color: C.textSm, marginTop: 6 }}>
                Рахується окремим процесом на сервері — вкладку можна закрити, кожне
                пораховане вікно вже збережено. Пошук за розкладом цьому не заважає.
              </div>
              {stabProgress && stabProgress.rows && stabProgress.rows.length > 0 && (
                <PeriodsTable periods={stabProgress.rows} showAverages />
              )}
            </div>
          )}

          {stabError && (
            <div style={{ fontSize: 12, color: '#B91C1C', background: '#FEE2E2',
                          borderRadius: 6, padding: '8px 12px' }}>
              {stabError}
            </div>
          )}

          {/* Підсумок ПО ВСІХ перевірках: найгірше вікно з усіх запусків — саме
              воно і є оцінкою, бо кожен запуск додає нові випадкові вікна. */}
          {stabRunsV2.length > 0 && (() => {
            const all = stabRunsV2.flatMap(r => (r.periods || []).filter(p => p && !p.error));
            if (!all.length) return null;
            const ys = all.map(p => Number(p.yearly_return) || 0);
            const worst = Math.min(...ys);
            const prof  = ys.filter(y => y > 0).length;
            const worstDd = Math.max(...all.map(p => Number(p.max_drawdown) || 0));
            const mean = ys.reduce((a, b) => a + b, 0) / ys.length;
            return (
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, marginBottom: 14 }}>
                {[
                  ['Найгірший період', `${worst.toFixed(2)}%/рік`, worst > 0 ? '#059669' : '#B91C1C'],
                  ['Середня по всіх', `${mean.toFixed(2)}%/рік`, mean > 0 ? '#059669' : '#B91C1C'],
                  ['Прибуткових періодів', `${prof}/${ys.length}`,
                   prof === ys.length ? '#059669' : '#B45309'],
                  ['Найгірша просадка', `${worstDd.toFixed(1)}%`, C.textLg],
                  ['Перевірок', `${stabRunsV2.length}`, C.textLg],
                ].map(([k, v, color]) => (
                  <div key={k} style={{ border: `2px solid ${C.border}`, borderRadius: 8,
                                        padding: '8px 14px', minWidth: 130, background: C.bgAlt }}>
                    <div style={{ fontSize: 11, color: C.textSm }}>{k}</div>
                    <div style={{ fontSize: 16, fontWeight: 700, color }}>{v}</div>
                  </div>
                ))}
              </div>
            );
          })()}

          {[...stabRunsV2].reverse().map(run => {
            const v = run.verdict || {};
            const open = stabOpen == null ? run.run === Math.max(...stabRunsV2.map(r => r.run || 0))
                                          : stabOpen === run.run;
            const s = run.settings || {};
            return (
              <div key={run.run} style={{ border: `1px solid ${C.border}`, borderRadius: 8,
                                          marginBottom: 10, overflow: 'hidden' }}>
                <div
                  onClick={() => setStabOpen(open ? -1 : run.run)}
                  style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap',
                           padding: '8px 12px', background: C.bgAlt, cursor: 'pointer' }}
                >
                  <span style={{ fontSize: 12, fontWeight: 700, color: C.textLg }}>
                    {open ? '▾' : '▸'} Перевірка #{run.run}
                  </span>
                  <span style={{ fontSize: 11, color: C.textSm }}>
                    {String(run.at || '').replace('T', ' ').slice(0, 16)}
                    {s.period_months ? ` · ${s.period_months} міс. × ${(run.periods || []).length}` : ''}
                    {s.mode === 'sweep' ? ' (усі точки входу)' : ''}
                    {' · '}{run.secs} с
                  </span>
                  <span style={{ fontSize: 12, fontWeight: 700,
                                 color: v.worst_yearly_pct > 0 ? '#059669' : '#B91C1C' }}>
                    найгірший {v.worst_yearly_pct}%/рік
                  </span>
                  <span style={{ fontSize: 12, color: C.textMd }}>
                    прибуткових {v.n_profitable}/{v.n_runs}
                  </span>
                  {v.capital_ladder_monotonic === false && (
                    <span style={{ fontSize: 11, color: '#B45309' }}>
                      ⚠️ драбина немонотонна
                    </span>
                  )}
                </div>

                {open && (
                  <div style={{ padding: '12px' }}>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, marginBottom: 14 }}>
                      {[
                        ['Найгірший період', `${v.worst_yearly_pct}%/рік`,
                         v.worst_yearly_pct > 0 ? '#059669' : '#B91C1C'],
                        ['Медіанний', `${v.median_yearly_pct}%/рік`, C.textLg],
                        ['Прибуткових', `${v.n_profitable}/${v.n_runs}`,
                         v.n_profitable === v.n_runs ? '#059669' : '#B45309'],
                        ['Найгірша просадка', `${v.worst_drawdown_pct}%`, C.textLg],
                        ['Найменше угод', `${v.min_trades}`, C.textLg],
                      ].map(([k, val, color]) => (
                        <div key={k} style={{ border: `1px solid ${C.border}`, borderRadius: 8,
                                              padding: '8px 14px', minWidth: 130 }}>
                          <div style={{ fontSize: 11, color: C.textSm }}>{k}</div>
                          <div style={{ fontSize: 16, fontWeight: 700, color }}>{val}</div>
                        </div>
                      ))}
                    </div>

                    {v.capital_ladder_monotonic === false && (
                      <div style={{ fontSize: 12, color: C.textMd, background: '#FEF3C7',
                                    borderRadius: 6, padding: '8px 12px', marginBottom: 14,
                                    borderLeft: '3px solid #F59E0B' }}>
                        ⚠️ Драбина капіталу немонотонна ({v.capital_inversions} інверсій):
                        більший рахунок подекуди «заробляє» краще за менший без жодного порядку.
                        Це означає, що підсумок вирішують кілька окремих угод, і різниця між цією
                        стратегією та сусідньою в таблиці нічого не означає.
                      </div>
                    )}

                    {/* Та сама таблиця, що зверху сторінки, плюс середні під нею
                        і «+» на кожному рядку. */}
                    <PeriodsTable
                      periods={run.periods || []}
                      showAverages
                      onExtend={run.run === Math.max(...stabRunsV2.map(r => r.run || 0))
                        ? extendStability : undefined}
                      extendBusy={stabExtendBusy}
                    />

                    {(run.capital_rows || []).length > 0 && (
                      <div style={{ marginTop: 14 }}>
                        <div style={{ fontSize: 12, fontWeight: 700, color: C.textLg, marginBottom: 4 }}>
                          Драбина капіталу — на власному вікні рядка
                        </div>
                        <div style={{ fontSize: 11, color: C.textSm, marginBottom: 6, maxWidth: 760 }}>
                          Питання тут одне: чи не гойдає результат сама лише зміна суми.
                          Якщо гойдає без порядку — підсумок вирішують кілька окремих
                          угод. ⚠️ З показником у шапці результату ці числа збігатись
                          не мусять: перевірка веде всесвіт по кожному бару, а рядок
                          рахувався зі своїм налаштуванням.
                        </div>
                        <table style={{ borderCollapse: 'collapse', fontSize: 12 }}>
                          <thead>
                            <tr style={{ background: C.bgAlt }}>
                              {['Капітал', 'Фінал $', '%/рік', 'Просадка', 'Угод'].map(h => (
                                <th key={h} style={{ textAlign: 'left', padding: '6px 10px',
                                                     borderBottom: `1px solid ${C.border}`,
                                                     color: C.textSm, fontWeight: 600 }}>{h}</th>
                              ))}
                            </tr>
                          </thead>
                          <tbody>
                            {(run.capital_rows || []).map((r, i) => (
                              <tr key={i} style={{ borderBottom: `1px solid ${C.border}` }}>
                                <td style={{ padding: '6px 10px', fontWeight: 600 }}>{r.label}</td>
                                <td style={{ padding: '6px 10px' }}>
                                  {r.final_balance != null ? '$' + Math.round(r.final_balance).toLocaleString('en-US') : '—'}
                                </td>
                                <td style={{ padding: '6px 10px', fontWeight: 600,
                                             color: (r.yearly_return || 0) > 0 ? '#059669' : '#B91C1C' }}>
                                  {r.yearly_return != null ? Number(r.yearly_return).toFixed(2) : '—'}
                                </td>
                                <td style={{ padding: '6px 10px' }}>
                                  {r.max_drawdown != null ? Number(r.max_drawdown).toFixed(1) + '%' : '—'}
                                </td>
                                <td style={{ padding: '6px 10px' }}>{r.sells ?? '—'}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}

                    <div style={{ marginTop: 8, fontSize: 11, color: C.textSm }}>
                      {(run.periods || []).length} періодів за {run.secs} с
                    </div>
                  </div>
                )}
              </div>
            );
          })}

          {/* Перевірки за попередньою методикою. У підсумок вони не входять —
              міряли інше, — але лишаються доступними: рахувати їх наново вже
              нічим, стару методику прибрано. */}
          {stabRunsLegacy.length > 0 && (
            <div style={{ marginTop: 10, borderTop: `1px dashed ${C.border}`, paddingTop: 10 }}>
              <div style={{ fontSize: 11, color: C.textSm }}>
                Перевірок за попередньою методикою (капітал / склад всесвіту /
                нарізка головного вікна): <b>{stabRunsLegacy.length}</b>. Їхні числа
                непорівнянні з новими, тому в підсумок не входять.
                {' '}
                <button
                  type="button"
                  onClick={() => setStabLegacyOpen(v => !v)}
                  style={{ border: 'none', background: 'none', padding: 0, fontSize: 11,
                           color: C.primary, cursor: 'pointer', textDecoration: 'underline' }}
                >
                  {stabLegacyOpen ? 'сховати' : 'показати'}
                </button>
              </div>

              {stabLegacyOpen && [...stabRunsLegacy].reverse().map(run => {
                const v = run.verdict || {};
                return (
                  <div key={`legacy-${run.run}`}
                       style={{ border: `1px solid ${C.border}`, borderRadius: 8,
                                marginTop: 10, overflow: 'hidden' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap',
                                  padding: '8px 12px', background: '#FEF3C7' }}>
                      <span style={{ fontSize: 12, fontWeight: 700, color: C.textLg }}>
                        Перевірка #{run.run} · стара методика
                      </span>
                      <span style={{ fontSize: 11, color: C.textSm }}>
                        {String(run.at || '').replace('T', ' ').slice(0, 16)}
                        {run.seed != null ? ` · зерно ${run.seed}` : ''}
                        {run.secs != null ? ` · ${run.secs} с` : ''}
                      </span>
                      <span style={{ fontSize: 12, fontWeight: 700,
                                     color: v.worst_yearly_pct > 0 ? '#059669' : '#B91C1C' }}>
                        найгірша {v.worst_yearly_pct}%/рік
                      </span>
                      <span style={{ fontSize: 12, color: C.textMd }}>
                        прибуткових {v.n_profitable}/{v.n_runs}
                      </span>
                    </div>
                    <div style={{ padding: 12 }}>
                      <div style={{ fontSize: 11, color: C.textMd, background: '#FFFBEB',
                                    borderRadius: 6, padding: '6px 10px', marginBottom: 10,
                                    borderLeft: '3px solid #F59E0B' }}>
                        ⚠️ Ці рядки міряли <b>інше</b>: капітал від 100 тис. до 5 млн, три
                        випадкові підвибірки всесвіту та три підвікна <b>головного</b>
                        періоду. Нова перевірка бере випадкові вікна по всій історії, веде
                        всесвіт по кожному бару й рахує три реалістичні суми. Порівнювати
                        числа між двома методиками не можна.
                      </div>
                      <div style={{ overflowX: 'auto' }}>
                        <table style={{ width: '100%', borderCollapse: 'collapse',
                                        fontSize: 12, minWidth: 640 }}>
                          <thead>
                            <tr style={{ background: C.bgAlt }}>
                              {['Збурення', 'Капітал', 'Тікерів', 'Фінал $', '%/рік', 'Просадка', 'Угод']
                                .map(h => (
                                  <th key={h} style={{ textAlign: 'left', padding: '6px 10px',
                                                       borderBottom: `1px solid ${C.border}`,
                                                       color: C.textSm, fontWeight: 600 }}>{h}</th>
                                ))}
                            </tr>
                          </thead>
                          <tbody>
                            {(run.rows || []).map((r, i) => (
                              <tr key={i} style={{ borderBottom: `1px solid ${C.border}` }}>
                                {/* Межі відрізка беремо з полів рядка, а не з готового
                                    підпису: найстаріші збережені перевірки несуть підпис
                                    до місяця, хоча самі дати в них повні. */}
                                <td style={{ padding: '6px 10px', color: C.textMd }}>
                                  {r.axis === 'period' && r.start && r.stop
                                    ? `період ${String(r.start).slice(0, 10)} → ${String(r.stop).slice(0, 10)}`
                                    : r.label}
                                </td>
                                <td style={{ padding: '6px 10px' }}>
                                  {r.capital != null ? '$' + Math.round(r.capital).toLocaleString('en-US') : '—'}
                                </td>
                                <td style={{ padding: '6px 10px' }}>{r.n_tickers ?? '—'}</td>
                                <td style={{ padding: '6px 10px' }}>
                                  {r.final_equity != null ? '$' + Math.round(r.final_equity).toLocaleString('en-US') : '—'}
                                </td>
                                <td style={{ padding: '6px 10px', fontWeight: 600,
                                             color: (r.yearly_pct || 0) > 0 ? '#059669' : '#B91C1C' }}>
                                  {r.yearly_pct != null ? Number(r.yearly_pct).toFixed(2) : '—'}
                                </td>
                                <td style={{ padding: '6px 10px' }}>
                                  {r.max_drawdown_pct != null ? Number(r.max_drawdown_pct).toFixed(1) + '%' : '—'}
                                </td>
                                <td style={{ padding: '6px 10px' }}>{r.trades ?? '—'}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* «Перевірка стійкості» ПОВЕРНУТО 15.09 на запит власника — 1 в 1, як було
          до 08.09 (випадкові вікна з усієї історії, «ще раз» додає нові).
          Драбина умов «плече × обіг» лишається прибраною (margin_runs у базі). */}

      {!embedded && !nested && (
        <div style={{ ...S.card, padding: '18px 22px', marginTop: 4 }}>
          <div style={{ fontSize: 15, fontWeight: 700, color: C.textLg, marginBottom: 6 }}>
            Драбина варіантів
          </div>
          <div style={{ fontSize: 12, color: C.textSm, marginBottom: 12, maxWidth: 860 }}>
            Проганяє <b>цю саму</b> стратегію на діапазоні значень однієї осі. Кожен
            щабель рахується на всіх перевірочних вікнах рядка, тож відповідає не
            «скільки вийде», а «як часто буває погано і наскільки глибоко».
            <br />
            Числа щаблів у підсумок рядка <b>не входять</b>: інша сума — це вже інша
            стратегія, а не додаткові докази про цю. Осі рахуються по одній; потрібен
            перетин — запусти двічі.
          </div>

          {/* Осі незалежні: можна ввімкнути всі три й порахувати одним
              натисканням — вийдуть ТРИ окремі драбини, а не перетин. */}
          {[
            { key: 'capital',   title: 'Інша сума',              hint: null },
            { key: 'companies', title: 'Інша кількість компаній',
              hint: 'розмір позиції рухається разом із кількістю (100/n)' },
            { key: 'leverage',  title: 'Кредитне плече',
              hint: 'та сама галочка вмикає й фільтр «ціна < $5 не купувати»' },
            { key: 'hold_to_cost', title: 'Hold to Cost (HTC Bars)',
              hint: 'макс. барів очікування до продажу на збиток' },
          ].map(({ key, title, hint }) => {
            const r = varAxes[key];
            const set = (patch) => setVarAxes(prev => ({ ...prev, [key]: { ...prev[key], ...patch } }));
            const a2 = Number(r.from), b2 = Number(r.to), st = Number(r.step);
            const steps = (st > 0 && b2 >= a2)
              ? Math.round((b2 - a2) / st) + 1 + (r.withOff && a2 > 0 ? 1 : 0) : 0;
            return (
              <div key={key} style={{ display: 'flex', alignItems: 'center', gap: 8,
                                      flexWrap: 'wrap', padding: '5px 0',
                                      opacity: r.on ? 1 : 0.55 }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: 6,
                                minWidth: 230, cursor: 'pointer', fontSize: 13 }}>
                  <input type="checkbox" checked={r.on}
                         onChange={e => set({ on: e.target.checked })} />
                  <span style={{ fontWeight: r.on ? 600 : 400 }}>{title}</span>
                </label>
                <span style={{ fontSize: 12, color: C.textSm }}>від</span>
                <input type="number" value={r.from} disabled={!r.on}
                  onChange={e => set({ from: e.target.value })}
                  style={{ width: 100, padding: '5px 7px', fontSize: 13,
                           border: `1px solid ${C.border}`, borderRadius: 5,
                           background: r.on ? '#fff' : '#F1F5F9' }} />
                <span style={{ fontSize: 12, color: C.textSm }}>до</span>
                <input type="number" value={r.to} disabled={!r.on}
                  onChange={e => set({ to: e.target.value })}
                  style={{ width: 100, padding: '5px 7px', fontSize: 13,
                           border: `1px solid ${C.border}`, borderRadius: 5,
                           background: r.on ? '#fff' : '#F1F5F9' }} />
                <span style={{ fontSize: 12, color: C.textSm }}>крок</span>
                <input type="number" value={r.step} disabled={!r.on}
                  onChange={e => set({ step: e.target.value })}
                  style={{ width: 90, padding: '5px 7px', fontSize: 13,
                           border: `1px solid ${C.border}`, borderRadius: 5,
                           background: r.on ? '#fff' : '#F1F5F9' }} />
                {key === 'hold_to_cost' && (
                  <label style={{ display: 'flex', alignItems: 'center', gap: 4,
                                  fontSize: 12, color: C.textMd,
                                  cursor: r.on ? 'pointer' : 'default' }}>
                    <input type="checkbox" checked={!!r.withOff} disabled={!r.on}
                           onChange={e => set({ withOff: e.target.checked })} />
                    + без HTC (baseline)
                  </label>
                )}
                <span style={{ fontSize: 11, color: C.textSm }}>
                  {r.on ? `${steps} щаблів` : ''}{r.on && hint ? ` · ${hint}` : ''}
                </span>
              </div>
            );
          })}

          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 10,
                        flexWrap: 'wrap' }}>
            <span style={{ fontSize: 13, minWidth: 230 }}>Виключити тікери</span>
            <input type="text" value={varExclude}
              onChange={e => setVarExclude(e.target.value)}
              placeholder="напр. REPL BURU SMMT"
              title="Цих паперів для прогону не існує взагалі — не «не купувати», а «немає у всесвіті». Відповідає на питання, чи тримається підсумок на одному-двох іменах: кількість компаній цього не покаже, вона міняє лише вагу позиції."
              style={{ flex: 1, minWidth: 240, padding: '5px 7px', fontSize: 13,
                       border: `1px solid ${C.border}`, borderRadius: 5 }} />
            <span style={{ fontSize: 11, color: C.textSm }}>
              можна без жодної осі — вийде один щабель «без цих імен»
            </span>
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 8,
                        flexWrap: 'wrap' }}>
            <select value={varWindows} onChange={e => setVarWindows(e.target.value)}
              title="На яких вікнах міряти щаблі"
              style={{ padding: '5px 8px', fontSize: 13, borderRadius: 5,
                       border: `1px solid ${C.border}` }}>
              <option value="base">на базових вікнах</option>
              <option value="all">на всіх накопичених</option>
            </select>
            <button onClick={runVariants} disabled={varBusy || !varSteps || !varWindowCount}
              style={{ padding: '5px 14px', fontSize: 13, fontWeight: 600,
                       background: (varBusy || !varSteps || !varWindowCount) ? '#94A3B8' : '#2563EB',
                       color: '#fff', border: 'none', borderRadius: 5,
                       cursor: varBusy ? 'default' : 'pointer' }}>
              {varBusy ? 'Запускаю…' : 'Порахувати драбини'}
            </button>
            <span style={{ fontSize: 11, color: C.textMd }}>
              {varSteps
                ? `≈ ${(varSteps * (1 + varWindowCount)).toLocaleString()} симуляцій `
                  + `(${varSteps} щаблів × ${varWindowCount} вікон)`
                : 'Не ввімкнено жодної осі або порожній діапазон'}
              {varWindowCount === 0 ? ' · у рядка немає перевірочних вікон' : ''}
            </span>
          </div>
          {varMsg && <div style={{ fontSize: 12, color: C.success, marginTop: 6 }}>✓ {varMsg}</div>}
          {varErr && <div style={{ fontSize: 12, color: C.danger, marginTop: 6 }}>⚠️ {varErr}</div>}

          {/* Пачка в роботі: смужка прогресу і щаблі, що вже пораховані. */}
          {varLive && (
            <div style={{ marginTop: 12, padding: '10px 12px', border: `1px solid #BFDBFE`,
                          background: '#EFF6FF', borderRadius: 6 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                <span style={{ fontSize: 12, fontWeight: 700, color: '#1E40AF' }}>
                  Рахується…
                </span>
                <div style={{ width: 160, height: 6, background: '#DBEAFE', borderRadius: 3,
                              overflow: 'hidden' }}>
                  <div style={{ width: `${varLive.wTotal ? (varLive.wDone / varLive.wTotal * 100)
                                          : varLive.total ? (varLive.done / varLive.total * 100) : 0}%`,
                                height: '100%', background: '#2563EB', transition: 'width .3s' }} />
                </div>
                <span style={{ fontSize: 12, color: C.textMd }}>
                  {varLive.done} з {varLive.total} щаблів
                  {varLive.wTotal ? ` · вікон ${varLive.wDone || 0} з ${varLive.wTotal}` : ''}
                </span>
              </div>
              {(varLive.ladders || []).map((L, li) => (
                ((L.rows || []).length > 0 || (L.pending || []).length > 0) && (
                  <div key={li} style={{ marginTop: 8, overflowX: 'auto' }}>
                    <div style={{ fontSize: 11, fontWeight: 700, color: C.textMd, marginBottom: 3 }}>
                      {{ capital: 'Сума', companies: 'Кількість компаній',
                         leverage: 'Плече', hold_to_cost: 'Hold to Cost',
                         exclude: 'Виключення тікерів' }[L.axis] || L.axis}
                      {' '}· {(L.rows || []).length} з {L.total}
                    </div>
                    <div style={{ background: '#fff' }}>
                      <VariantLadderTable rows={L.rows || []} pending={L.pending || []} />
                    </div>
                  </div>
                )
              ))}
            </div>
          )}

          {variantRuns.length > 0 && (
            <div style={{ marginTop: 14 }}>
              {variantRuns.slice().reverse().map((run, ri) => {
                const idx = variantRuns.length - 1 - ri;
                const open = varOpen == null ? ri === 0 : varOpen === idx;
                const axisName = { capital: 'Сума', companies: 'Кількість компаній',
                                   leverage: 'Плече', hold_to_cost: 'Hold to Cost',
                                   exclude: 'Виключення тікерів' }[run.axis] || run.axis;
                return (
                  <div key={idx} style={{ border: `1px solid ${C.border}`, borderRadius: 6,
                                          marginBottom: 8 }}>
                    <div onClick={() => setVarOpen(open ? -1 : idx)}
                      style={{ padding: '7px 10px', cursor: 'pointer', display: 'flex',
                               alignItems: 'center', gap: 8, background: '#F8FAFC' }}>
                      <span style={{ fontWeight: 700, fontSize: 13 }}>
                        {open ? '▾' : '▸'} {axisName}
                      </span>
                      <span style={{ fontSize: 11, color: C.textSm }}>
                        {(run.rows || []).length} щаблів · {run.n_windows} вікон ·
                        {run.windows === 'all' ? ' усі накопичені' : ' базові'} · {run.at}
                      </span>
                    </div>
                    {open && (
                      <div style={{ overflowX: 'auto' }}>
                        <VariantLadderTable rows={run.rows || []} />
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}
      </div>

    </div>
  );
}

// Таблиця щаблів драбини варіантів. Одна й та сама і для готового запуску, і
// для пачки, що ще рахується: щаблі зʼявляються в ній построково, а ще не
// пораховані стоять рядком «рахується…» на своєму місці.
function VariantLadderTable({ rows, pending = [] }) {
  // Готові й ще не пораховані щаблі — один список у порядку значення осі, щоб
  // рядок не перескакував, коли щабель дораховується.
  const all = [...rows.map(r => ({ r })), ...pending.map(p => ({ p, r: p }))]
    .sort((a, b) => (a.r.value == null) - (b.r.value == null)
                    || (a.r.value ?? 0) - (b.r.value ?? 0));
  return (
    <table style={{ borderCollapse: 'collapse', fontSize: 12, width: '100%' }}>
      <thead>
        <tr>
          {/* Назви беруться з ГОЛОВНОЇ таблиці результатів
              (analyzer/shared.js COLUMN_GROUPS) — один
              показник має зватись однаково скрізь, інакше
              «Ø у прибуткових» і «Average % in no worst Yr»
              читаються як два різні числа, хоч це одне.
              «Значення» і «Головне вікно Yr%» лишаються —
              у головній таблиці відповідників не мають. */}
          {['Значення', 'Periods', 'Avg. Yr%', 'Worst Yr%',
            '% of worst Yr', 'Average % in worst Yr',
            'Best Yr%', '% of no worst Yr',
            'Average % in no worst Yr', 'MaxDD',
            'Головне вікно Yr%'].map(h => (
            <th key={h} style={{ padding: '6px 9px', background: '#F1F5F9',
                                 border: `1px solid ${C.border}`,
                                 whiteSpace: 'nowrap', textAlign: 'left' }}>{h}</th>
          ))}
        </tr>
      </thead>
      <tbody>
        {all.map(({ r, p }, i) => p ? (
          <tr key={i} style={{ color: C.textSm }}>
            <td style={{ padding: '6px 9px', border: `1px solid ${C.border}`,
                         fontWeight: 700, whiteSpace: 'nowrap' }}>{p.label}</td>
            <td colSpan={10} style={{ padding: '6px 9px', border: `1px solid ${C.border}`,
                                      fontStyle: 'italic', background: '#F8FAFC' }}>
              рахується…
            </td>
          </tr>
        ) : (
          <tr key={i}>
            <td style={{ padding: '6px 9px', border: `1px solid ${C.border}`,
                         fontWeight: 700, whiteSpace: 'nowrap' }}>{r.label}</td>
            <td style={{ padding: '6px 9px', border: `1px solid ${C.border}` }}>{r.n_windows ?? '—'}</td>
            <td style={{ padding: '6px 9px', border: `1px solid ${C.border}`,
                         color: (r.avg_yearly ?? 0) >= 0 ? C.success : C.danger,
                         fontWeight: 600 }}>
              {r.avg_yearly != null ? fmtAnnualPct(r.avg_yearly, 1) : '—'}</td>
            <td style={{ padding: '6px 9px', border: `1px solid ${C.border}`,
                         color: (r.worst_yearly ?? 0) >= 0 ? C.success : C.danger }}>
              {r.worst_yearly != null ? fmtAnnualPct(r.worst_yearly, 1) : '—'}</td>
            <td style={{ padding: '6px 9px', border: `1px solid ${C.border}` }}>
              {r.neg_share != null ? r.neg_share.toFixed(1) + '%' : '—'}</td>
            <td style={{ padding: '6px 9px', border: `1px solid ${C.border}`, color: C.danger }}>
              {r.neg_avg != null ? fmtAnnualPct(r.neg_avg, 1) : '—'}</td>
            <td style={{ padding: '6px 9px', border: `1px solid ${C.border}` }}>
              {r.best_yearly != null ? fmtAnnualPct(r.best_yearly, 1) : '—'}</td>
            <td style={{ padding: '6px 9px', border: `1px solid ${C.border}` }}>
              {r.pos_share != null ? r.pos_share.toFixed(1) + '%' : '—'}</td>
            <td style={{ padding: '6px 9px', border: `1px solid ${C.border}`, color: C.success }}>
              {r.pos_avg != null ? fmtAnnualPct(r.pos_avg, 1) : '—'}</td>
            <td style={{ padding: '6px 9px', border: `1px solid ${C.border}` }}>
              {r.worst_max_drawdown != null ? r.worst_max_drawdown.toFixed(1) + '%' : '—'}</td>
            <td style={{ padding: '6px 9px', border: `1px solid ${C.border}`, color: C.textSm }}>
              {r.main_yearly != null ? fmtAnnualPct(r.main_yearly, 1) : '—'}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

// ── Period Summary View ───────────────────────────────────────────────────────

function PeriodSummaryView({ period, periodIdx, parentData, fullDetail, loading, errorMsg, onRecalc, onClearDetail, onBack, onTickerClick, bulkRecalcProgress, queueIndex }) {
  if (!period) return null;
  const n = periodIdx + 1;
  const hasError = period.error === true;

  const MetricRow = ({ label, value, color }) => (
    <div style={{
      display: 'flex', justifyContent: 'space-between', alignItems: 'center',
      padding: '9px 0', borderBottom: `1px solid ${C.border}`,
    }}>
      <span style={{ fontSize: 13, color: C.textSm }}>{label}</span>
      <span style={{ fontSize: 14, fontWeight: 700, color: color || C.text }}>{value}</span>
    </div>
  );

  const colorFor = (v, goodPositive = true) => {
    if (v == null || !isFinite(v)) return C.textSm;
    return goodPositive ? (v >= 0 ? C.success : C.danger) : (v <= 0 ? C.success : C.danger);
  };

  const syntheticData = fullDetail ? {
    id:              `${parentData.id}-p${periodIdx}`,
    params:          fullDetail.params,
    metrics:         fullDetail.metrics,
    trades:          fullDetail.trades || [],
    equity_curve:    fullDetail.equity_curve || [],
    buy_count:       fullDetail.buy_count,
    sell_count:      fullDetail.sell_count,
    data_type:       fullDetail.data_type,
    start_time:      fullDetail.start_time,
    stop_time:       fullDetail.stop_time,
    name:            fullDetail.name,
    periods_results: [],
  } : null;

  return (
    <div>
      <div style={S.backBar}>
        <button style={S.backBtn} onClick={onBack}>← Back to main</button>
        <div style={S.title}>Period {n}: {period.start} — {period.end} ({period.days} days)</div>
      </div>

      {/* Параметри стратегії — ті самі, що на головному періоді (запит менеджера
          07.09). Період міняє лише дати, тож дивитись на угоди вікна, не бачачи
          правил, за якими вони зроблені, доводилось із перемиканням назад.
          Беремо params самого періоду, коли він уже порахований (там дати цього
          вікна), інакше — параметри рядка. */}
      {(() => {
        const pParams = fullDetail?.params || parentData?.params;
        if (!pParams) return null;
        return (
          <div style={{ ...S.card, padding: '12px 16px', marginBottom: 12 }}>
            <StrategyParamsLabel params={{ ...pParams, ...(parentData?._meta || {}) }} />
          </div>
        );
      })()}

      {hasError ? (
        <div style={{
          ...S.card,
          padding: '24px 20px', textAlign: 'center', color: C.danger, fontSize: 14,
        }}>
          ⚠️ Calculation failed for this period (insufficient data or date range).
        </div>
      ) : (
        <div style={{ ...S.card, padding: '20px 24px' }}>
          <div style={{ maxWidth: 480 }}>
            <MetricRow
              label="Yearly return"
              value={fmtAnnualPct(period.yearly_return, 1)}
              color={colorFor(period.yearly_return)}
            />
            <MetricRow
              label="Monthly return"
              value={fmtAnnualPct(period.monthly_return, 2)}
              color={colorFor(period.monthly_return)}
            />
            <MetricRow
              label="Total return"
              value={fmtPct(period.total_return)}
              color={colorFor(period.total_return)}
            />
            <MetricRow
              label="Win rate"
              value={period.win_rate != null ? period.win_rate.toFixed(1) + '%' : '—'}
              color={period.win_rate != null ? (period.win_rate >= 60 ? C.success : period.win_rate >= 40 ? C.text : C.danger) : C.textSm}
            />
            {(() => {
              // Prefer freshly-computed values (fullDetail), else the persisted
              // period summary (stored on the row after a sim run / recalc).
              const fm = fullDetail?.metrics || {};
              const sharpeVal = fm.sharpe_ratio ?? period.sharpe;
              const maxddVal  = fm.max_drawdown ?? period.max_drawdown;
              const buyVal    = fullDetail?.buy_count ?? period.buys;
              return (
                <>
                  <MetricRow label="Sharpe ratio"
                    value={sharpeVal != null ? Number(sharpeVal).toFixed(2) : '—'}
                    color={sharpeVal != null ? (sharpeVal >= 1 ? C.success : C.text) : C.textSm} />
                  <MetricRow label="Max drawdown"
                    value={maxddVal != null ? Number(maxddVal).toFixed(1) + '%' : '—'}
                    color={maxddVal != null ? (maxddVal <= 20 ? C.text : C.danger) : C.textSm} />
                  <MetricRow label="Buy count"
                    value={buyVal != null ? buyVal : '—'} />
                </>
              );
            })()}
            <MetricRow
              label="Final balance"
              value={period.final_balance != null ? fmtUsd(period.final_balance) : '—'}
              color={period.final_balance != null ? colorFor(period.final_balance - 100000) : C.textSm}
            />
          </div>

          <div style={{ marginTop: 24, borderTop: `1px solid ${C.border}`, paddingTop: 20 }}>
            {loading ? (
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <span style={{
                  display: 'inline-block', width: 18, height: 18,
                  border: `2px solid ${C.border}`, borderTopColor: C.primary,
                  borderRadius: '50%', animation: 'spin 0.8s linear infinite',
                }} />
                <span style={{ fontSize: 13, color: C.textMd }}>Розрахунок повного результату...</span>
              </div>
            ) : errorMsg ? (
              <div>
                {/* Таймаут тут ≠ втрачений розрахунок. Задача рахується у воркері
                    й зберігає period_details у базу НЕЗАЛЕЖНО від того, чи дочекався
                    HTTP-запит. Тому окремий, спокійний текст: перше відкриття періоду
                    справді довге (завантаження всього вікна + прогрів ядра), а друге
                    бере готове з бази. Показувати це червоною помилкою з «Спробувати
                    знову» — означає штовхати користувача рахувати те саме ще раз. */}
                {/timeout|timed out|408/i.test(String(errorMsg)) ? (
                  <div style={{
                    background: '#FEF3C7', border: '1px solid #F59E0B', borderRadius: 7,
                    padding: '10px 16px', color: '#92400E', fontSize: 13, marginBottom: 10,
                  }}>
                    ⏳ Розрахунок триває довше за очікування запиту — але він <b>не
                    скасований</b>: результат дорахується у фоні й збережеться.
                    Поверніться на цю вкладку за хвилину-дві, і графік зʼявиться вже
                    готовим. Перше відкриття періоду найдовше: вантажиться все головне
                    вікно й прогрівається ядро; наступні — миттєві.
                  </div>
                ) : (
                  <div style={{
                    background: C.dangerLt, border: `1px solid ${C.danger}`, borderRadius: 7,
                    padding: '10px 16px', color: C.danger, fontSize: 13, marginBottom: 10,
                  }}>
                    ⚠️ {errorMsg}
                  </div>
                )}
                <button
                  onClick={onRecalc}
                  style={{
                    padding: '8px 18px', borderRadius: 7, fontSize: 13, fontWeight: 600,
                    cursor: 'pointer', background: C.primary, color: '#fff', border: 'none',
                  }}
                >
                  Спробувати знову
                </button>
              </div>
            ) : fullDetail ? (
              <button
                onClick={onRecalc}
                style={{
                  padding: '4px 12px', borderRadius: 6, fontSize: 12, fontWeight: 500,
                  cursor: 'pointer', background: 'transparent', color: C.textSm,
                  border: `1px solid ${C.border}`,
                }}
                title="Запустити повторний розрахунок цього періоду"
              >
                ↺ Перерахувати
              </button>
            ) : bulkRecalcProgress != null && queueIndex > bulkRecalcProgress.done ? (
              <div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <span style={{
                    display: 'inline-block', width: 16, height: 16, flexShrink: 0,
                    border: `2px solid ${C.border}`, borderTopColor: '#F59E0B',
                    borderRadius: '50%', animation: 'spin 0.8s linear infinite',
                  }} />
                  <span style={{ fontSize: 13, color: C.textMd, fontWeight: 500 }}>
                    Розрахунок виконується…
                  </span>
                  <span style={{ fontSize: 12, color: C.textSm }}>
                    ({bulkRecalcProgress.done}/{bulkRecalcProgress.total} завершено)
                  </span>
                </div>
                <div style={{ marginTop: 8, fontSize: 12, color: C.textSm }}>
                  Результат з'явиться автоматично після завершення розрахунку цього та попередніх періодів.
                </div>
              </div>
            ) : (
              <div>
                <button
                  onClick={onRecalc}
                  style={{
                    padding: '10px 22px', height: 40, borderRadius: 8, fontSize: 13, fontWeight: 600,
                    cursor: 'pointer', background: C.primary, color: '#fff', border: 'none',
                  }}
                >
                  Розрахувати повністю
                </button>
                <div style={{ marginTop: 8, fontSize: 12, color: C.textSm }}>
                  Перерахує цей період повністю й покаже графік, угоди та equity нижче. Зазвичай 35-60 с, перший раз після перезапуску сервера — до 1.5 хв.
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {syntheticData && (
        <div style={{ marginTop: 24, borderTop: `2px solid ${C.border}`, paddingTop: 20 }}>
          <BacktestDetail
            data={syntheticData}
            onBack={onBack}
            onTickerClick={onTickerClick}
            nested
          />
        </div>
      )}
    </div>
  );
}
